import React, { useState, useEffect, useCallback, useRef, useMemo, createContext, useContext, lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { isEquityOpen } from "./lib/marketClock";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler, Tooltip, Legend } from "chart.js";
import { Line, Bar } from "react-chartjs-2";

// ─── Extracted Components ─────────────────────────────────────────────────────
// SwipeCards2/3, DiamondUI and (further below) FloorChat are each large,
// mutually-exclusive-at-a-time screens (only one mobile UI mode ever renders,
// only one desktop tab is ever active) that used to all be eager static
// imports — every one of them shipped in the single main bundle regardless of
// which a given user actually used, which is most of why this app's bundle
// was ~940KB gzipped for, say, someone who only ever opens Diamond UI. lazy()
// + <Suspense> below defers each to its own chunk, fetched only when that
// mode/tab is actually selected. SwipeCards (v1) stays a static import since
// AiExitVerdictModal, a separate named export from the same module, is used
// by the desktop Trading Hub tab regardless — splitting just the default
// export out would still pull the whole module in eagerly for that.
import SwipeCards, { AiExitVerdictModal } from "./components/SwipeCards";
const SwipeCards2 = lazy(() => import("./components/SwipeCards2"));
const SwipeCards3 = lazy(() => import("./components/SwipeCards3"));
const DiamondUI = lazy(() => import("./components/diamond/DiamondUI"));
import TradingHubTab from "./components/TradingHubTab";
import AnalyticsTab from "./components/AnalyticsTab";
import SettingsTab from "./components/SettingsTab";
import OptionAnalysisModal from "./components/OptionAnalysisModal";
import LiveScannerFeed from "./components/LiveScannerFeed";
import UniversalScanner from "./components/UniversalScanner";
import PerformanceDashboard from "./components/PerformanceDashboard";
import TradeHistoryCard from "./components/TradeHistoryCard";
import LiveMarketDashboard from "./components/LiveMarketDashboard";
import OCIControlCenter from "./components/OCIControlCenter";
import QuickTradeModal from "./components/QuickTradeModal";
import TrailStopModal from "./components/TrailStopModal";
import { OmegaWatchRoom } from "./components/Positions";
import { ActiveOrdersStrip } from "./components/modals/ActiveOrdersStrip";
import LandingPage from "./LandingPage";
import AdminLogin from "./AdminLogin";
import LlmStatusLight from "./components/LlmStatusLight";
import OmegaTeaPage from "./components/OmegaTeaPage";
import { PreloadProvider } from "./components/PreloadContext";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import OmegaEnhancementsCard from "./components/OmegaEnhancementsCard";
import AskOmegaFAB from "./components/AskOmegaFAB";
import { LinkageMapCard, DepthExplainer } from "./components/OmegaCanon";
import OmegaZiPortfolioCards from "./components/OmegaZiPortfolioCards";
import OmegaTea from "./components/OmegaTea";
import ZBucksDashboard from "./components/ZBucksDashboard";
import RealTradePreviewModal from "./components/RealTradePreviewModal";
import ZiChatExpanded from "./components/modals/ZiChatExpanded";
import DocsTab from "./components/DocsTab";
// Lazy — see the note by the SwipeCards2/3/DiamondUI imports above. Only
// rendered when the desktop "floor" tab is active.
const FloorChat = lazy(() => import("./components/FloorChat"));
import ErrorBoundary from "./components/ErrorBoundary";
import TickerCard from "./components/TickerCard";
import PwaInstallBanner from "./components/PwaInstallBanner";
import ThemeManager from "./components/ThemeManager";
import SharePickButton from "./components/SharePickButton";
import GuardianToast from "./components/GuardianToast";
import { prefetch, getCached } from "./lib/symbolCache";
import { fastGet, fastGetAsync, prewarm } from "./lib/fastFetch";
import { openSSE } from "./lib/sse";
import IntelTab from "./components/IntelTab";
import StrategyBuilder from "./components/StrategyBuilder";
import BrokerSettingsPanel from "./components/BrokerSettingsPanel";
import RiskTab from "./components/RiskTab";
import Btc15Tab from "./components/Btc15Tab";
import OmegaAIPicksPanel from "./components/OmegaAIPicksPanel";
import AutopilotPanel from "./components/AutopilotPanel";
import FlowPanel from "./components/FlowPanel";
import { AuthContext, useAuth } from "./context/AuthContext";
import AccountTab from "./components/AccountTab";
import PositionDetailModal from "./components/PositionDetailModal";
import { fmt$, fmtPct, fmtN } from "./lib/format";
import { formatAiText } from "./lib/utils";
import { bootGate, useBootGate, isDiamondMobileBoot, BOOT_FILL_MS, BOOT_UNMOUNT_MS, BOOT_SAFETY_MS } from "./lib/bootGate";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler, Tooltip, Legend);

// Platform watermark suppressed via index.css: #emergent-badge { display:none !important }

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Branded loading screen — the Omega mark + an indeterminate progress
// sweep, used everywhere the app has a genuine "not ready yet" moment:
// the auth-check boot gate, the OAuth callback exchange, and the
// <Suspense> fallback for the lazy-loaded mobile UI modes/FloorChat (see
// their imports above) while that mode's own chunk is still being
// fetched. One shared component so "still loading" always reads as this
// app instead of a generic blank spinner.
// `overlay` + `done` back a second use: MobileWorkspace pins this on top of
// a mobile screen that has already mounted (and started fetching) so the
// bar can visibly finish — fill to 100% and fade — once that screen's own
// first data round trip lands, instead of the screen underneath quietly
// filling in its own separate spinners after this one has already vanished.
function OmegaLoadingScreen({ label = "Loading", overlay = false, done = false }) {
  return (
    <div className={`omega-loading-screen${overlay ? " omega-loading-screen--overlay" : ""}${done ? " omega-loading-screen--done" : ""}`}>
      <div className="omega-loading-aurora" aria-hidden="true" />
      <div className="omega-loading-grid" aria-hidden="true" />
      <div className="omega-loading-content">
        <div className="omega-loading-logo-wrap">
          <img src="/icon-192.png" alt="" className="omega-loading-logo" />
        </div>
        <div className="omega-loading-bar-track"><div className="omega-loading-bar-fill" /></div>
        <span className="omega-loading-label">{done ? "Ready" : label}</span>
      </div>
    </div>
  );
}
const LazyScreenFallback = () => <OmegaLoadingScreen label="Loading" />;
// Named boot milestones for MobileWorkspace's Diamond overlay (see
// DiamondUI's onBootStage) — real, sequential facts about what's actually
// happened so far, not a manufactured percentage.
const BOOT_STAGE_LABELS = {
  auth: "Signing you in",
  restored: "Restoring the floor",
  connecting: "Connecting to the floor",
  roster: "Loading the desk roster",
  board: "Syncing the leaderboard",
  wire: "Opening the live wire",
  history: "Catching up on the floor",
};

// ─── Axios interceptor: cookie auth + CSRF header ──────────────────────────────
// Auth is carried entirely by the httpOnly access_token cookie (withCredentials
// sends it automatically). The csrf_token cookie itself is NOT httpOnly, but
// the frontend (ziprobability.com) and backend (zero22-app.onrender.com) are
// different origins in production, so document.cookie can never actually see
// a cookie the backend set — a hard browser same-origin boundary, not a config
// bug. Login-establishing endpoints (/auth/login, /admin/auth, /auth/session)
// and /auth/me now return csrfToken in the JSON body instead, which IS
// readable cross-origin; storeCsrfToken() below stashes it in localStorage so
// the interceptor can echo it back on mutating requests. We still fall back
// to reading the cookie for same-origin setups (e.g. local dev).
const CSRF_STORAGE_KEY = "roi_csrf_token";

function storeCsrfToken(userData) {
  try {
    if (userData?.csrfToken) localStorage.setItem(CSRF_STORAGE_KEY, userData.csrfToken);
  } catch {}
}

function getCsrfToken() {
  try {
    const stored = localStorage.getItem(CSRF_STORAGE_KEY);
    if (stored) return stored;
  } catch {}
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

axios.interceptors.request.use((config) => {
  config.withCredentials = true;
  const method = (config.method || "get").toLowerCase();
  if (method !== "get" && method !== "head") {
    const csrf = getCsrfToken();
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

// ─── Auth Context (moved to ./context/AuthContext) ────────────────────────────

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Defer to AuthCallback when there's a session_id to exchange — it'll
    // call setUser itself once that (currently disabled, 410) flow resolves.
    const hasOAuthHash = window.location.hash?.includes("session_id=");
    if (hasOAuthHash) {
      setLoading(false);
      return;
    }
    // Auth is cookie-based now — just ask the server. No client-readable
    // token to check first; a missing/expired cookie simply 401s.
    try {
      const res = await axios.get(`${API}/auth/me`);
      storeCsrfToken(res.data);
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Preload-on-login (iter 93) — prime the server-side caches for expensive
  // endpoints the user is about to hit (Elite universe, Crypto universe,
  // Decision Journal, AI health). Fire-and-forget so UI stays fast.
  // Keyed off user_id (a stable primitive), not the whole user object — a
  // profile-save elsewhere calling setUser({...}) with a new object reference
  // for the SAME user would otherwise re-fire all four warm-up calls and
  // restart the 90s interval for no reason.
  const userId = user?.user_id;
  useEffect(() => {
    if (!userId) return;
    prewarm([
      `${API}/elite/universe?max_per_ticker=2&include_crypto=true`,
      `${API}/crypto/universe?limit=12`,
      `${API}/autopilot/decisions?limit=30`,
      `${API}/daily-pnl`,
    ]);
    // Light refresh every 90 s while logged in so caches never feel stale.
    const iv = setInterval(() => {
      axios.get(`${API}/crypto/universe?limit=12`).catch(() => {});
      axios.get(`${API}/elite/universe?max_per_ticker=2&include_crypto=true`).catch(() => {});
    }, 90000);
    return () => clearInterval(iv);
  }, [userId]);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    // Clear the new-location toast dedup key so the next login from a new IP
    // fires the security warning fresh rather than being silently suppressed.
    try { sessionStorage.removeItem("omega.lastLogin.newLocationToast.v1"); } catch {}
    try { localStorage.removeItem(CSRF_STORAGE_KEY); } catch {}
    setUser(null);
  }, []);

  // Memoized so consumers of useAuth() only re-render when user/loading
  // actually change, not on every AuthProvider render for unrelated reasons.
  const value = useMemo(() => ({ user, setUser, loading, logout, checkAuth }), [user, loading, logout, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Login Page (wraps LandingPage) ──────────────────────────────────────────
function LoginPage() {
  const { setUser } = useAuth();

  const handleLogin = async (username, password) => {
    const res = await axios.post(`${API}/auth/login`, { username, password });
    storeCsrfToken(res.data);
    setUser(res.data);
  };

  return <LandingPage onLogin={handleLogin} />;
}

// ─── Auth Callback ────────────────────────────────────────────────────────────
function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const processed = React.useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) { navigate("/", { replace: true }); return; }

    const sessionId = match[1];
    (async () => {
      try {
        const res = await axios.post(`${API}/auth/session`, { session_id: sessionId });
        storeCsrfToken(res.data);
        setUser(res.data);
        navigate("/", { replace: true, state: { user: res.data } });
      } catch {
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return <OmegaLoadingScreen label="Signing in" />;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────




// ─── Common US symbols for instant client-side autocomplete ───────────────────
const SYMBOL_LIST = [
  ["AAPL","Apple Inc","EQUITY"],["MSFT","Microsoft Corp","EQUITY"],["NVDA","NVIDIA Corp","EQUITY"],
  ["AMZN","Amazon.com Inc","EQUITY"],["GOOGL","Alphabet Inc","EQUITY"],["META","Meta Platforms","EQUITY"],
  ["TSLA","Tesla Inc","EQUITY"],["AMD","Advanced Micro Devices","EQUITY"],["PLTR","Palantir Technologies","EQUITY"],
  ["HOOD","Robinhood Markets","EQUITY"],["ORCL","Oracle Corp","EQUITY"],["NFLX","Netflix Inc","EQUITY"],
  ["CRM","Salesforce Inc","EQUITY"],["ADBE","Adobe Inc","EQUITY"],["INTC","Intel Corp","EQUITY"],
  ["QCOM","Qualcomm Inc","EQUITY"],["AVGO","Broadcom Inc","EQUITY"],["TXN","Texas Instruments","EQUITY"],
  ["MU","Micron Technology","EQUITY"],["AMAT","Applied Materials","EQUITY"],["LRCX","Lam Research","EQUITY"],
  ["KLAC","KLA Corp","EQUITY"],["MRVL","Marvell Technology","EQUITY"],["ARM","Arm Holdings","EQUITY"],
  ["SNOW","Snowflake Inc","EQUITY"],["DDOG","Datadog Inc","EQUITY"],["NET","Cloudflare Inc","EQUITY"],
  ["ZS","Zscaler Inc","EQUITY"],["CRWD","CrowdStrike Holdings","EQUITY"],["PANW","Palo Alto Networks","EQUITY"],
  ["NOW","ServiceNow Inc","EQUITY"],["WDAY","Workday Inc","EQUITY"],["TEAM","Atlassian Corp","EQUITY"],
  ["MDB","MongoDB Inc","EQUITY"],["GTLB","GitLab Inc","EQUITY"],["COIN","Coinbase Global","EQUITY"],
  ["MSTR","MicroStrategy Inc","EQUITY"],["RBLX","Roblox Corp","EQUITY"],["UBER","Uber Technologies","EQUITY"],
  ["LYFT","Lyft Inc","EQUITY"],["ABNB","Airbnb Inc","EQUITY"],["DASH","DoorDash Inc","EQUITY"],
  ["SHOP","Shopify Inc","EQUITY"],["SQ","Block Inc","EQUITY"],["PYPL","PayPal Holdings","EQUITY"],
  ["V","Visa Inc","EQUITY"],["MA","Mastercard Inc","EQUITY"],["GS","Goldman Sachs","EQUITY"],
  ["JPM","JPMorgan Chase","EQUITY"],["BAC","Bank of America","EQUITY"],["WFC","Wells Fargo","EQUITY"],
  ["MS","Morgan Stanley","EQUITY"],["C","Citigroup Inc","EQUITY"],["BRK.B","Berkshire Hathaway","EQUITY"],
  ["JNJ","Johnson & Johnson","EQUITY"],["PFE","Pfizer Inc","EQUITY"],["MRNA","Moderna Inc","EQUITY"],
  ["LLY","Eli Lilly","EQUITY"],["ABBV","AbbVie Inc","EQUITY"],["UNH","UnitedHealth Group","EQUITY"],
  ["CVX","Chevron Corp","EQUITY"],["XOM","Exxon Mobil","EQUITY"],["BP","BP plc","EQUITY"],
  ["BA","Boeing Co","EQUITY"],["CAT","Caterpillar Inc","EQUITY"],["DE","Deere & Co","EQUITY"],
  ["GE","GE Aerospace","EQUITY"],["RTX","RTX Corp","EQUITY"],["LMT","Lockheed Martin","EQUITY"],
  ["AMGN","Amgen Inc","EQUITY"],["GILD","Gilead Sciences","EQUITY"],["BIIB","Biogen Inc","EQUITY"],
  ["WMT","Walmart Inc","EQUITY"],["TGT","Target Corp","EQUITY"],["COST","Costco Wholesale","EQUITY"],
  ["HD","Home Depot","EQUITY"],["LOW","Lowe's Companies","EQUITY"],["NKE","Nike Inc","EQUITY"],
  ["DIS","Walt Disney Co","EQUITY"],["CMCSA","Comcast Corp","EQUITY"],["T","AT&T Inc","EQUITY"],
  ["VZ","Verizon Communications","EQUITY"],["TMUS","T-Mobile US","EQUITY"],
  ["SPY","SPDR S&P 500 ETF","ETF"],["QQQ","Invesco QQQ Trust","ETF"],["IWM","iShares Russell 2000","ETF"],
  ["DIA","SPDR Dow Jones","ETF"],["VTI","Vanguard Total Market","ETF"],["GLD","SPDR Gold Shares","ETF"],
  ["SLV","iShares Silver Trust","ETF"],["TLT","iShares 20+ Yr Treasury","ETF"],["XLF","Financial Select SPDR","ETF"],
  ["XLK","Technology Select SPDR","ETF"],["XLE","Energy Select SPDR","ETF"],["SQQQ","ProShares UltraPro Short QQQ","ETF"],
  ["TQQQ","ProShares UltraPro QQQ","ETF"],["SPXU","ProShares UltraPro Short S&P500","ETF"],
  ["RIVN","Rivian Automotive","EQUITY"],["LCID","Lucid Group","EQUITY"],["NIO","NIO Inc","EQUITY"],
  ["XPEV","XPeng Inc","EQUITY"],["LI","Li Auto","EQUITY"],["F","Ford Motor","EQUITY"],["GM","General Motors","EQUITY"],
  ["SOFI","SoFi Technologies","EQUITY"],["AFRM","Affirm Holdings","EQUITY"],["UPST","Upstart Holdings","EQUITY"],
  ["OPEN","Opendoor Technologies","EQUITY"],["DKNG","DraftKings Inc","EQUITY"],["PENN","PENN Entertainment","EQUITY"],
  ["GME","GameStop Corp","EQUITY"],["AMC","AMC Entertainment","EQUITY"],["BB","BlackBerry Ltd","EQUITY"],
  ["SNDL","SNDL Inc","EQUITY"],["CLOV","Clover Health","EQUITY"],["WISH","ContextLogic","EQUITY"],
  ["IONQ","IonQ Inc","EQUITY"],["RGTI","Rigetti Computing","EQUITY"],["QUBT","Quantum Computing","EQUITY"],
  ["SMCI","Super Micro Computer","EQUITY"],["DELL","Dell Technologies","EQUITY"],["HPQ","HP Inc","EQUITY"],
  ["IBM","IBM Corp","EQUITY"],["CSCO","Cisco Systems","EQUITY"],["JNPR","Juniper Networks","EQUITY"],
].map(([symbol, name, type]) => ({ symbol, name, type }));

// ─── Symbol search bar with autocomplete dropdown ─────────────────────────────
function SymbolSearchBar({ value, onChange, onSelect, onSubmit }) {
  const [results, setResults] = React.useState([]);
  const [open, setOpen]       = React.useState(false);
  const [active, setActive]   = React.useState(-1);
  const [rect, setRect]       = React.useState(null);
  const inputRef              = React.useRef(null);
  const dropRef               = React.useRef(null);

  // Instant client-side filter
  React.useEffect(() => {
    const q = value.trim().toUpperCase();
    if (!q) { setResults([]); setOpen(false); return; }
    const exact   = SYMBOL_LIST.filter(s => s.symbol.startsWith(q));
    const nameHit = SYMBOL_LIST.filter(s => !s.symbol.startsWith(q) && s.name.toUpperCase().includes(q));
    const merged  = [...exact, ...nameHit].slice(0, 8);
    setResults(merged);
    setActive(-1);
    if (merged.length && inputRef.current) {
      setRect(inputRef.current.getBoundingClientRect());
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [value]);

  // Close on outside click
  React.useEffect(() => {
    const handler = e => {
      if (
        inputRef.current && !inputRef.current.contains(e.target) &&
        dropRef.current && !dropRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const select = item => {
    setOpen(false);
    setResults([]);
    onSelect(item.symbol);
  };

  const handleKey = e => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, -1)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); select(results[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  const typeColor = t => t === "EQUITY" ? "#60a5fa" : t === "ETF" ? "#a78bfa" : t === "CRYPTOCURRENCY" ? "#fbbf24" : "var(--muted)";

  // Dropdown rendered via fixed position so no parent overflow can clip it
  const dropdown = open && results.length > 0 && rect ? (
    <div
      ref={dropRef}
      style={{
        position: "fixed",
        top: rect.bottom + 6,
        left: rect.left,
        width: Math.max(rect.width, 320),
        zIndex: 99999,
        background: "#0f172a",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 14,
        boxShadow: "0 20px 60px rgba(0,0,0,0.7)",
        overflow: "hidden",
      }}
    >
      {results.map((item, i) => (
        <div
          key={item.symbol}
          onMouseDown={e => { e.preventDefault(); select(item); }}
          onMouseEnter={() => setActive(i)}
          style={{
            display: "flex", alignItems: "center", gap: 10, padding: "11px 16px",
            cursor: "pointer",
            background: i === active ? "rgba(96,165,250,0.12)" : "transparent",
            borderBottom: i < results.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 2 }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: "#f1f5f9" }}>{item.symbol}</span>
              {item.type && (
                <span style={{ fontSize: 8, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: typeColor(item.type) + "22", color: typeColor(item.type), letterSpacing: 0.5, textTransform: "uppercase" }}>
                  {item.type}
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</div>
          </div>
          {item.exchange && <div style={{ fontSize: 10, color: "#475569", flexShrink: 0 }}>{item.exchange}</div>}
        </div>
      ))}
    </div>
  ) : null;

  return (
    <>
      <form onSubmit={e => { setOpen(false); onSubmit(e); }} className="desktop-shell-search">
        <input
          ref={inputRef}
          className="inp desktop-shell-search-input"
          value={value}
          onChange={onChange}
          onFocus={() => { if (results.length && inputRef.current) { setRect(inputRef.current.getBoundingClientRect()); setOpen(true); } }}
          onKeyDown={handleKey}
          placeholder="Search ticker…"
          autoComplete="off"
          data-testid="ticker-search"
        />
      </form>
      {dropdown && createPortal(dropdown, document.body)}
    </>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isLocalDevHost =
    typeof window !== "undefined" &&
    /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== "undefined" && localStorage.getItem("zi_mobileUiMode") === "floor") return "floor";
    return "account";
  });
  const [teaTicker, setTeaTicker] = useState(null); // deep-link target for Omega Tea
  const [health, setHealth] = useState(null);
  const [regime, setRegime] = useState(null);
  const [quoteData, setQuoteData] = useState({});
  const [searchTicker, setSearchTicker] = useState("");
  const [tickerView, setTickerView] = useState(null);
  const prefetchTimer = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [notifRefreshing, setNotifRefreshing] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [mobileUiMode, setMobileUiModeState] = useState(() => {
    const savedMode = localStorage.getItem("zi_mobileUiMode");
    if (savedMode) return savedMode;
    // Default for a brand-new account with no saved preference. "swipe3"
    // (Trading Floor / "glass") is the actively-developed mobile surface —
    // "swipe" (classic) and "swipe2" have had no real feature work since
    // the app's first week, so a new user should land where the ongoing
    // polish actually is, not on the stalest of the three.
    return isLocalDevHost ? "advanced" : "swipe3";
  }); // "swipe3" or "advanced"
  const setMobileUiMode = (m) => { localStorage.setItem("zi_mobileUiMode", m); setMobileUiModeState(m); window.dispatchEvent(new CustomEvent("zi-ui-mode-changed")); if (m === "floor") setActiveTab("floor"); };
  useEffect(() => {
    const exitFocus = () => {
      localStorage.setItem("zi_mobileUiMode", "advanced");
      setMobileUiModeState("advanced");
      window.dispatchEvent(new CustomEvent("zi-ui-mode-changed"));
      setActiveTab("account");
    };
    window.addEventListener("zi-exit-focus", exitFocus);
    return () => window.removeEventListener("zi-exit-focus", exitFocus);
  }, []);
  const [notifDetail, setNotifDetail] = useState(null);
  const [notifFilter, setNotifFilter] = useState("all");
  const [notifSearch, setNotifSearch] = useState("");
  const [pinnedNotifs, setPinnedNotifs] = useState(() => { try { return new Set(JSON.parse(localStorage.getItem("zi_pinned_notifs") || "[]")); } catch { return new Set(); } });
  const [dismissedNotifs, setDismissedNotifs] = useState(() => { try { return new Set(JSON.parse(localStorage.getItem("zi_dismissed_notifs") || "[]")); } catch { return new Set(); } });
  const [sideDrawerOpen, setSideDrawerOpen] = useState(false);
  const [theme, setThemeState] = useState(() => localStorage.getItem("zi_theme") || "auto");
  const [showOmegaBar, setShowOmegaBar] = useState(() => localStorage.getItem("zi_showOmegaBar") !== "false");
  const [isOffline, setIsOffline] = useState(() => typeof navigator !== "undefined" && navigator.onLine === false);
  const [swUpdateReady, setSwUpdateReady] = useState(false);

  // Register service worker + listen for online/offline + SW update detection.
  // A browser only checks service-worker.js for changes on its own schedule
  // (roughly once per navigation, sometimes as rarely as every 24h) — a tab
  // left open across a deploy can sit on stale cached JS for hours with the
  // "New version ready" toast never even appearing, since nothing ever asked
  // the browser to look. Explicitly calling reg.update() right after
  // registration, and again every time the tab comes back to the foreground
  // (the moment a user would actually expect to see today's fix), makes that
  // check happen promptly instead of relying on the browser's own cadence.
  useEffect(() => {
    if ("serviceWorker" in navigator && !isLocalDevHost) {
      window.addEventListener("load", () => {
        navigator.serviceWorker.register("/service-worker.js").then(reg => {
          // Detect when a new SW is waiting to activate
          const checkUpdate = () => {
            if (!reg.installing && !reg.waiting) return;
            const sw = reg.installing || reg.waiting;
            sw.addEventListener("statechange", () => {
              if (sw.state === "installed" && navigator.serviceWorker.controller) {
                setSwUpdateReady(true);
              }
            });
          };
          checkUpdate();
          reg.addEventListener("updatefound", checkUpdate);
          reg.update().catch(() => {});
          const onVisible = () => { if (document.visibilityState === "visible") reg.update().catch(() => {}); };
          document.addEventListener("visibilitychange", onVisible);
        }).catch(() => {});
      });
    }
    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, [isLocalDevHost]);

  // Localhost should always use the freshest dev bundle — clear any old SW/cache.
  useEffect(() => {
    if (!isLocalDevHost || !("serviceWorker" in navigator)) return;
    navigator.serviceWorker.getRegistrations()
      .then((regs) => Promise.all(regs.map((reg) => reg.unregister())))
      .catch(() => {});
    if (typeof caches !== "undefined") {
      caches.keys()
        .then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
        .catch(() => {});
    }
  }, [isLocalDevHost]);

  // Fire browser notification for new notifications across ALL high-priority
  // categories (iter 87) — user requested that desktop mirrors mobile push coverage.
  // Previously only "risk" + "alert" fired; now intel (news, earnings, AI decisions),
  // order (fills), trade, tea, and scan all produce a desktop pop-up when unread.
  const [notifiedIds, setNotifiedIds] = useState(() => new Set());
  useEffect(() => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const PRIORITY_CATS = new Set(["risk", "alert", "intel", "order", "trade", "tea", "scan"]);
    const newItems = notifications.filter(n =>
      !n.read &&
      PRIORITY_CATS.has(n.category) &&
      !notifiedIds.has(n.key || n._id || n.id)
    );
    if (newItems.length === 0) return;
    newItems.forEach(n => {
      try {
        const icon = "/icon-192.png";
        const notif = new Notification(n.title || "Omega ZI Alert", {
          body: (n.message || "").slice(0, 180),
          icon,
          badge: icon,
          tag: `${n.category}-${n.type || ""}`,
          requireInteraction: n.category === "risk" || n.category === "alert",
          silent: false,
        });
        notif.onclick = () => { window.focus(); notif.close(); };
      } catch {}
    });
    setNotifiedIds(ids => {
      const s = new Set(ids);
      newItems.forEach(n => s.add(n.key || n._id || n.id));
      return s;
    });
  }, [notifications, notifiedIds]);
  
  // Lifted autopilot state — persists across tab switches
  const [apMode, setApModeGlobal] = useState(() => localStorage.getItem("zi_apMode") || "OBSERVE_ONLY");
  const [ziThreshold, setZiThresholdGlobal] = useState(() => Number(localStorage.getItem("zi_ziThreshold")) || 60);

  // Hero KPI strip state (lifted from AccountTab so it shows on every tab).
  // AccountTab.js used to also poll GET /portfolio independently on its own
  // 30s timer — same endpoint, unsynchronized — doubling backend load while
  // the Account tab was open and letting the hero strip and the tab's own
  // equity cards disagree for up to 30s after a real balance change. It now
  // reads this same state via props instead (see <AccountTab> below).
  const [portfolio, setPortfolio] = useState(null);
  const [portfolioStale, setPortfolioStale] = useState(false);
  const [globalDailyPnl, setGlobalDailyPnl] = useState(null);

  // Omega System State
  const [omegaStatus, setOmegaStatus] = useState(null);
  const [autonomyLevel, setAutonomyLevel] = useState("pro");

  const loadOmegaStatus = async () => {
    try {
      const r = await axios.get(`${API}/omega/status`);
      setOmegaStatus(r.data);
      if (r.data.autonomyLevel) setAutonomyLevel(r.data.autonomyLevel);
    } catch {}
  };

  // Hero strip refresh — also exposed to AccountTab (see <AccountTab> below)
  // so an action that changes the real balance (placing/closing a trade) can
  // ask for an immediate refresh instead of waiting up to 30s for the next
  // scheduled tick.
  const refreshHero = () => {
    axios.get(`${API}/preload`).then(r => {
      const d = r.data;
      if (d.quotes) setQuoteData(d.quotes);
      if (d.regime) setRegime(d.regime);
      if (d.notifications) { setNotifications(d.notifications); setUnreadCount(d.unread || 0); }
      if (d.omega) { setOmegaStatus(d.omega); if (d.omega.autonomyLevel) setAutonomyLevel(d.omega.autonomyLevel); }
      if (d.portfolio) { setPortfolio(d.portfolio); setPortfolioStale(false); }
      else setPortfolioStale(true);
    }).catch(() => setPortfolioStale(true));
    fastGetAsync(`${API}/daily-pnl`).then(r => setGlobalDailyPnl(r.data)).catch(() => {});
  };

  const changeAutonomy = async (level) => {
    const prev = autonomyLevel;
    setAutonomyLevel(level);
    try {
      await axios.put(`${API}/omega/autonomy`, { level });
      loadOmegaStatus();
    } catch {
      setAutonomyLevel(prev);
    }
  };

  const toggleManualKillSwitch = async () => {
    const current = omegaStatus?.killSwitches?.switches?.find(s => s.switch === 5)?.fired || false;
    try {
      await axios.post(`${API}/omega/kill-switch/manual`, { active: !current });
      loadOmegaStatus();
      toast.success(current ? "Kill switch cleared — new entries allowed" : "Kill switch engaged — new entries halted");
    } catch {
      toast.error("Kill switch toggle failed — trading state unchanged. Try again.");
    }
  };

  const setApMode = async (mode) => {
    // Optimistic update with a real revert on failure — this toggle
    // includes FULL_AUTO (live real-money trading). The old version wrote
    // React state AND localStorage immediately, then fired the PUT with
    // `.catch(() => {})` and no success check at all: a failed request
    // (network blip, 403, 500) left the UI — including across reloads,
    // since localStorage was already written — showing a trading mode the
    // backend never actually applied, with zero indication to the user.
    // Same fix already applied correctly in MobileSettingsExpanded.js's
    // AutopilotQuickPanel.toggleOnOff; this closes the same gap here.
    const prevMode = apMode;
    setApModeGlobal(mode);
    localStorage.setItem("zi_apMode", mode);
    try {
      await axios.put(`${API}/settings`, { autopilotMode: mode });
    } catch {
      setApModeGlobal(prevMode);
      localStorage.setItem("zi_apMode", prevMode);
      toast.error(`Failed to switch to ${mode} — trading mode unchanged (still ${prevMode}). Try again.`);
    }
  };
  const setZiThreshold = (val) => {
    setZiThresholdGlobal(val);
    localStorage.setItem("zi_ziThreshold", String(val));
    axios.put(`${API}/settings`, { ziThreshold: val }).catch(() => {});
  };

  // Theme management: auto = light during market hours (9:30-16:00 ET), dark after.
  // Theme DOM application + persistence is centralised in <ThemeManager />
  // (App.js top-level). Dashboard only needs local state for the SettingsTab UI.
  // Sync local state when ThemeManager (or any other tab) changes the theme.
  useEffect(() => {
    const h = (e) => {
      const t = e?.detail?.theme;
      if (t && t !== theme) setThemeState(t);
    };
    window.addEventListener("zi-theme-changed", h);
    return () => window.removeEventListener("zi-theme-changed", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Omega status bar visibility — set from Settings (OmegaSettingsPanel's
  // Autopilot tab). Same localStorage + CustomEvent pattern as theme above,
  // since Settings is a separate mounted component with no shared state.
  useEffect(() => {
    const h = (e) => {
      const v = e?.detail?.visible;
      if (typeof v === "boolean") setShowOmegaBar(v);
    };
    window.addEventListener("zi-omega-bar-visibility-changed", h);
    return () => window.removeEventListener("zi-omega-bar-visibility-changed", h);
  }, []);

  // Theme is owned by <ThemeManager /> (mounted in App). Dashboard just
  // reads/sets local state for UI selection; the actual DOM mutation +
  // /api/user/state persistence happens centrally via the zi-theme-changed event.
  const setTheme = (t) => {
    setThemeState(t);
    try { localStorage.setItem("zi_theme", t); } catch {}
    try { window.dispatchEvent(new CustomEvent("zi-theme-changed", { detail: { theme: t } })); } catch {}
  };

  // Auto-save active tab changes
  const switchTab = (tabId) => {
    setActiveTab(tabId);
    axios.put(`${API}/user/state`, { activeTab: tabId }).catch(() => {});
  };

  // Load saved user state on mount
  useEffect(() => {
    axios.get(`${API}/settings`).then(r => {
      const s = r.data;
      if (s.autopilotMode) {
        setApModeGlobal(s.autopilotMode);
        localStorage.setItem("zi_apMode", s.autopilotMode);
      }
      if (s.ziThreshold) {
        setZiThresholdGlobal(s.ziThreshold);
        localStorage.setItem("zi_ziThreshold", String(s.ziThreshold));
      }
    }).catch(() => {});
    axios.get(`${API}/user/state`).then(r => {
      const s = r.data;
      const focusFloor = typeof window !== "undefined" && localStorage.getItem("zi_mobileUiMode") === "floor";
      // One-shot handoff from the mobile Trading Floor drawer's "Trade
      // Discovery" link — wins over the saved/floor tab since it's an
      // explicit, just-now user action.
      const pendingTab = typeof window !== "undefined" ? localStorage.getItem("zi_pending_tab") : null;
      if (pendingTab) {
        localStorage.removeItem("zi_pending_tab");
        setActiveTab(pendingTab);
      } else if (focusFloor) {
        setActiveTab("floor");
      } else if (isLocalDevHost) {
        setActiveTab("account");
        axios.put(`${API}/user/state`, { activeTab: "account" }).catch(() => {});
      } else if (s.activeTab) {
        setActiveTab(s.activeTab);
      }
      if (s.theme) { setThemeState(s.theme); localStorage.setItem("zi_theme", s.theme); }
    }).catch(() => {});
  }, [isLocalDevHost]);

  useEffect(() => {
    // OMEGA TURBO PRELOAD: Single call returns EVERYTHING — portfolio, quotes, omega status, kill switches, activity tape, forecasts
    axios.get(`${API}/preload`).then(r => {
      const d = r.data;
      if (d.quotes) setQuoteData(d.quotes);
      if (d.regime) setRegime(d.regime);
      if (d.notifications) { setNotifications(d.notifications); setUnreadCount(d.unread || 0); }
      if (d.omega) { setOmegaStatus(d.omega); if (d.omega.autonomyLevel) setAutonomyLevel(d.omega.autonomyLevel); }
      if (d.portfolio) { setPortfolio(d.portfolio); setPortfolioStale(false); }
      setHealth(d);
    }).catch(() => {
      axios.get(`${API}/health`).then(r => setHealth(r.data)).catch(() => {});
      loadRegime();
      loadOmegaStatus();
    });
    // Hero strip data (portfolio + daily P&L lifted to dashboard level)
    axios.get(`${API}/portfolio`).then(r => { setPortfolio(r.data); setPortfolioStale(false); }).catch(() => setPortfolioStale(true));
    fastGetAsync(`${API}/daily-pnl`).then(r => setGlobalDailyPnl(r.data)).catch(() => {});
    axios.post(`${API}/notifications/generate`).catch(() => {});
    const iv = setInterval(refreshHero, 30000);
    // Browsers throttle/pause setInterval in backgrounded tabs — a tab left
    // open overnight can silently miss every scheduled tick and only show
    // stale equity/buying-power again on the NEXT tick after refocus (up to
    // 30s later, or never if the interval got fully suspended). Refetch
    // immediately on refocus instead of waiting on the timer.
    const onVisible = () => { if (document.visibilityState === "visible") refreshHero(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  // SSE Quote stream
  useEffect(() => {
    // openSSE: silent-stream watchdog + reconnect (this had no onerror at all).
    const conn = openSSE(`${API}/stream/quotes?symbols=SPY,QQQ,AAPL,TSLA,NVDA`, {
      quotes: (d) => {
        const map = {};
        ((d && d.quotes) || []).forEach(q => { if (q.symbol) map[q.symbol] = q; });
        setQuoteData(prev => ({ ...prev, ...map }));
      },
    });
    return () => conn.close();
  }, []);

  // SSE Notifications stream — push trail-stop triggers + risk alerts in real-time
  useEffect(() => {
    // openSSE: silent-stream watchdog + reconnect (onerror here was a no-op).
    const conn = openSSE(`${API}/notifications/stream`, {
      snapshot: () => { /* ignore — already have initial via /preload */ },
      notification: (n) => {
      try {
        setNotifications(prev => {
          // De-dupe by key
          if (prev.some(p => (p.key || p._id) === (n.key || n._id))) return prev;
          return [n, ...prev];
        });
        setUnreadCount(c => c + 1);
        // Fire browser OS-level notification if granted
        if (typeof Notification !== "undefined" && Notification.permission === "granted" && (n.category === "risk" || n.category === "alert" || n.category === "intel")) {
          try {
            const icon = "/icon-192.png";
            const br = new Notification(n.title || "Omega ZI AI", {
              body: n.message || "", icon, badge: icon, tag: n.category,
              requireInteraction: n.category === "risk",
            });
            br.onclick = () => { window.focus(); br.close(); };
          } catch {}
        }
      } catch {}
      },
    });
    return () => conn.close();
  }, []);

  const loadRegime = async () => {
    try {
      const r = await axios.get(`${API}/zi/regime?symbol=SPY`);
      setRegime(r.data);
    } catch {}
  };


  const handleSearch = (e) => {
    e?.preventDefault();
    const sym = searchTicker.trim().toUpperCase();
    if (!sym) return;
    setTickerView({ symbol: sym });
  };

  const handleSearchInput = (e) => {
    const val = e.target.value.toUpperCase();
    setSearchTicker(val);
    if (prefetchTimer.current) clearTimeout(prefetchTimer.current);
    if (val.length >= 1 && val.length <= 6) {
      prefetchTimer.current = setTimeout(() => prefetch(val).catch(() => {}), 400);
    }
  };

  // ─── Omega Enhancement Ideas — action dispatcher ─────────────────────────
  // Routes every whitelisted backend action to a real UI operation.
  // No mocks: every branch hits a real endpoint or navigates to a real tab.
  const [enhanceTargetSubTab, setEnhanceTargetSubTab] = useState(null);
  const omegaDispatch = useCallback(async (action, payload, _idea) => {
    try {
      switch (action) {
        case "open_risk_dashboard":
          switchTab("risk"); break;
        case "open_analytics":
          if (payload && payload.sub) setEnhanceTargetSubTab(payload.sub);
          switchTab("analytics"); break;
        case "open_intel":
          switchTab("intel"); break;
        case "open_settings":
          switchTab("settings"); break;
        case "open_omega_tea":
          switchTab("tea"); break;
        case "open_options_hub":
          switchTab("hub"); break;
        case "open_recovery_plan":
          switchTab("risk"); break;
        case "open_ticker":
          if (payload?.symbol) {
            setTickerView({ symbol: String(payload.symbol).toUpperCase() });
          }
          break;
        case "run_auto_tune":
          try {
            const r = await axios.post(`${API}/hedge/auto-tune`);
            if (r.data?.ok === false) {
              toast.error(`Auto-tune needs more data: ${r.data?.reason || "no eligible samples"}`);
            } else if ((r.data?.samples || 0) === 0) {
              toast.success("Brain calibrated · defaults confirmed. Adaptive tuning unlocks after your first trades are recorded.");
            } else {
              toast.success(`Auto-tune complete · ${r.data.samples} trade samples processed`);
            }
          } catch (e) {
            toast.error(`Auto-tune failed: ${e.response?.data?.detail || e.message}`);
          }
          break;
        case "adjust_zi_threshold": {
          const to = Number(payload?.to);
          if (Number.isFinite(to) && to >= 40 && to <= 95) {
            setZiThreshold(to);
            try { await axios.put(`${API}/user/state`, { ziThreshold: to }); } catch {}
            toast.success(`Zi threshold set to ${to}`);
          }
          break;
        }
        default:
          break;
      }
    } catch (e) {
      toast.error(`Couldn't perform action: ${e.message || "unknown"}`);
    }
  }, [switchTab, setTickerView, setZiThreshold]);

  // Hover-prefetch: warm the cache for a tab before the user even clicks it.
  const TAB_PREFETCH = {
    account:   [`${API}/preload`, `${API}/daily-pnl`],
    crypto:    [`${API}/crypto/universe?limit=12`, `${API}/preload`],
    hub:       [`${API}/elite/universe?max_per_ticker=2&include_crypto=true`, `${API}/autopilot/positions`],
    intel:     [`${API}/omega/sensing/overview`, `${API}/omega/calibration`, `${API}/market/news/v2?symbol=SPY&limit=4&insight=false`],
    tea:       [`${API}/market/news/v2?symbol=SPY&limit=4&insight=false`, `${API}/market/news/v2?symbol=NVDA&limit=4&insight=false`],
    risk:      [`${API}/omega/kill-switches`, `${API}/pdt/status`, `${API}/risk/dashboard`],
    analytics: [`${API}/autopilot/performance`, `${API}/autopilot/outcomes?limit=20`],
    settings:  [`${API}/settings`, `${API}/omega/status`],
    zi:        [`${API}/floor-chat/roster`],
  };

  const tabs = [
    { id: "account", label: "Account Profile" },
    { id: "hub", label: "Trading Hub" },
    { id: "intel", label: "Intelligence" },
    { id: "tea", label: "Omega Tea" },
    { id: "risk", label: "Risk Dashboard" },
    { id: "analytics", label: "Analytics" },
    { id: "floor", label: "Trading Floor" },
    { id: "zi", label: "Zi Portfolios" },
    { id: "btc15", label: "BTC 15" },
    { id: "settings", label: "Settings" },
    { id: "docs", label: "How to Use" },
  ];

  const marketOpen = isEquityOpen(); // one clock — holidays and early closes included (lib/marketClock)
  const resolvedTheme = theme === "auto" ? (marketOpen ? "light" : "dark") : theme;
  const regimeLabel = regime ? `${(regime.regimeLabel || regime.regime || "—").replace(/_/g, " ")} ${Math.round((regime.confidence || 0) * 100)}%` : "—";
  const regimeTone = /bear/i.test(regimeLabel) ? "bear" : /bull/i.test(regimeLabel) ? "bull" : "neutral";

  return (
    <div className="app-container" data-testid="main-dashboard">
      {/* ═══ NOTIFICATION OVERLAY — RENDERED AT ROOT LEVEL ═══ */}
      {showNotifs && !notifDetail && (() => {
        const CAT_META = {
          all:   { label: "All",    color: "var(--muted)", icon: null },
          scan:  { label: "Scan",   color: "#3b82f6", icon: "Z" },
          trade: { label: "Trade",  color: "#4ade80", icon: "$" },
          risk:  { label: "Risk",   color: "#f87171", icon: "!" },
          alert: { label: "Alert",  color: "#a78bfa", icon: "A" },
          tea:   { label: "TEA",    color: "#f97316", icon: "T" },
          intel: { label: "Intel",  color: "#38bdf8", icon: "i" },
        };

        const pinNotif = (id) => setPinnedNotifs(prev => {
          const next = new Set(prev);
          next.has(id) ? next.delete(id) : next.add(id);
          localStorage.setItem("zi_pinned_notifs", JSON.stringify([...next]));
          return next;
        });
        const dismissNotif = (id) => setDismissedNotifs(prev => {
          const next = new Set(prev); next.add(id);
          localStorage.setItem("zi_dismissed_notifs", JSON.stringify([...next]));
          return next;
        });

        const allNotifs = notifications.filter(n => n.title);
        const visible = allNotifs.filter(n => {
          const id = n.id || n._id || n.key;
          if (dismissedNotifs.has(id)) return false;
          if (notifFilter !== "all" && n.category !== notifFilter) return false;
          if (notifSearch.trim()) {
            const q = notifSearch.toLowerCase();
            return (n.title || "").toLowerCase().includes(q) || (n.ticker || "").toLowerCase().includes(q) || (n.insight || n.message || "").toLowerCase().includes(q);
          }
          return true;
        });

        const pinned = visible.filter(n => pinnedNotifs.has(n.id || n._id || n.key));
        const unpinned = visible.filter(n => !pinnedNotifs.has(n.id || n._id || n.key));

        const now = new Date();
        const isToday = (d) => { if (!d) return false; const dt = new Date(d); return dt.toDateString() === now.toDateString(); };
        const todayItems = unpinned.filter(n => isToday(n.createdAt));
        const earlierItems = unpinned.filter(n => !isToday(n.createdAt));

        const catCounts = {};
        allNotifs.forEach(n => { catCounts[n.category] = (catCounts[n.category] || 0) + 1; });

        const NotifRow = ({ n, i }) => {
          const id = n.id || n._id || n.key || i;
          const isPinned = pinnedNotifs.has(id);
          const cm = CAT_META[n.category] || CAT_META.intel;
          const unread = !n.read;
          return (
            <div
              key={id}
              data-testid={`notif-item-${i}`}
              style={{
                display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px",
                borderRadius: 10, marginBottom: 3, cursor: "pointer",
                background: unread ? `${cm.color}08` : "transparent",
                borderLeft: `3px solid ${unread ? cm.color : "transparent"}`,
                border: `1px solid ${unread ? `${cm.color}22` : "var(--border)"}`,
                borderLeftWidth: 3,
                transition: "background 0.15s",
                position: "relative",
              }}
              onMouseEnter={e => e.currentTarget.style.background = `${cm.color}10`}
              onMouseLeave={e => e.currentTarget.style.background = unread ? `${cm.color}08` : "transparent"}
            >
              {/* Category avatar */}
              <div onClick={() => setNotifDetail(n)} style={{ width: 32, height: 32, borderRadius: 9, background: `${cm.color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: cm.color, flexShrink: 0, marginTop: 1 }}>
                {cm.icon}
              </div>

              {/* Body */}
              <div onClick={() => {
                if (n.type === "omega_tea" && n.ticker) {
                  setTeaTicker(n.ticker);
                  setActiveTab("tea");
                  if (n.id || n._id) {
                    axios.post(`${API}/notifications/read`, { id: n.id || n._id }).catch(() => {});
                  }
                  return;
                }
                setNotifDetail(n);
              }} data-testid={`notif-body-${i}`} style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text)" }}>{n.title}</span>
                  {n.ziScore && <span style={{ fontSize: 9, fontWeight: 800, color: n.ziScore >= 68 ? "#4ade80" : n.ziScore >= 53 ? "#60a5fa" : "#fbbf24" }}>Zi {n.ziScore}</span>}
                  {n.ziClass && <span className={`tag ${n.ziClass === "ULTRA_GEM" ? "t-bull" : "t-blue"}`} style={{ fontSize: 7 }}>{n.ziClass?.replace("_"," ")}</span>}
                  {isPinned && <span style={{ fontSize: 8, color: "#fbbf24" }}>📌</span>}
                </div>
                <div style={{ fontSize: 10, color: "var(--muted)", lineHeight: 1.4, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                  {n.insight || n.message}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 4, alignItems: "center" }}>
                  <span style={{ fontSize: 8, color: "var(--dim)" }}>{n.createdAt ? new Date(n.createdAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                  {n.ticker && <span style={{ fontSize: 8, fontWeight: 700, color: cm.color, background: `${cm.color}14`, padding: "1px 5px", borderRadius: 3 }}>{n.ticker}</span>}
                  <span style={{ fontSize: 8, color: "var(--dim)", background: "var(--layer1)", padding: "1px 5px", borderRadius: 3 }}>{cm.label}</span>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
                <button onClick={e => { e.stopPropagation(); setNotifDetail(n); }} title="Details"
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--dim)", fontSize: 10, padding: "2px 4px", borderRadius: 4, lineHeight: 1 }}>›</button>
                <button onClick={e => { e.stopPropagation(); pinNotif(id); }} title={isPinned ? "Unpin" : "Pin"}
                  style={{ background: "none", border: "none", cursor: "pointer", color: isPinned ? "#fbbf24" : "var(--dim)", fontSize: 10, padding: "2px 4px", borderRadius: 4, lineHeight: 1 }}>
                  {isPinned ? "★" : "☆"}
                </button>
                <button onClick={e => { e.stopPropagation(); dismissNotif(id); }} title="Dismiss"
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--dim)", fontSize: 10, padding: "2px 4px", borderRadius: 4, lineHeight: 1 }}>×</button>
              </div>
            </div>
          );
        };

        const SectionLabel = ({ label, count }) => (
          <div style={{ fontSize: 9, fontWeight: 700, color: "var(--dim)", textTransform: "uppercase", letterSpacing: "0.1em", padding: "8px 4px 4px", display: "flex", alignItems: "center", gap: 6 }}>
            {label} <span style={{ fontWeight: 400, color: "var(--dim)" }}>({count})</span>
          </div>
        );

        return (
          <>
          <div style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }} onClick={() => setShowNotifs(false)} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "min(860px, calc(100vw - 32px))", height: "min(700px, calc(100vh - 48px))", display: "flex", flexDirection: "column", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 20, zIndex: 9999, boxShadow: "0 32px 120px rgba(0,0,0,0.95), 0 0 0 1px rgba(255,255,255,0.06)" }} className="notif-panel" data-testid="notif-dropdown">

            {/* ── Header ── */}
            <div style={{ padding: "14px 16px 0", flexShrink: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 800, color: "var(--text)" }}>Notification Center</span>
                  {unreadCount > 0 && <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 10, background: "#3b82f6", color: "#fff" }}>{unreadCount} new</span>}
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {unreadCount > 0 && (
                    <button onClick={() => { axios.post(`${API}/notifications/read`).catch(() => {}); setUnreadCount(0); }}
                      style={{ fontSize: 10, padding: "4px 9px", borderRadius: 6, cursor: "pointer", border: "1px solid var(--border)", background: "var(--layer2)", color: "var(--muted)" }}>
                      Mark all read
                    </button>
                  )}
                  {dismissedNotifs.size > 0 && (
                    <button onClick={() => { setDismissedNotifs(new Set()); localStorage.removeItem("zi_dismissed_notifs"); }}
                      style={{ fontSize: 10, padding: "4px 9px", borderRadius: 6, cursor: "pointer", border: "1px solid var(--border)", background: "var(--layer2)", color: "var(--muted)" }}>
                      Restore {dismissedNotifs.size}
                    </button>
                  )}
                  <button onClick={() => setShowNotifs(false)} style={{ background: "var(--layer2)", border: "1px solid var(--border)", color: "var(--text)", width: 30, height: 30, borderRadius: 8, cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="notif-close">×</button>
                </div>
              </div>

              {/* Category stat chips */}
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 10 }}>
                {Object.entries(CAT_META).map(([key, meta]) => {
                  const count = key === "all" ? allNotifs.length : (catCounts[key] || 0);
                  if (key !== "all" && count === 0) return null;
                  const active = notifFilter === key;
                  return (
                    <button key={key} onClick={() => setNotifFilter(key)} style={{
                      display: "flex", alignItems: "center", gap: 4,
                      fontSize: 10, padding: "4px 9px", borderRadius: 20, cursor: "pointer", fontWeight: 600,
                      border: active ? `1px solid ${meta.color}` : "1px solid var(--border)",
                      background: active ? `${meta.color}18` : "var(--layer2)",
                      color: active ? meta.color : "var(--muted)",
                    }}>
                      {meta.label}
                      <span style={{ fontSize: 9, fontWeight: 800, padding: "0px 4px", borderRadius: 8,
                        background: active ? `${meta.color}30` : "var(--layer1)",
                        color: active ? meta.color : "var(--dim)" }}>{count}</span>
                    </button>
                  );
                })}
              </div>

              {/* Search */}
              <div style={{ position: "relative", marginBottom: 10 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--dim)" strokeWidth="2" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                <input
                  value={notifSearch} onChange={e => setNotifSearch(e.target.value)}
                  placeholder="Search alerts, tickers..."
                  style={{ width: "100%", padding: "7px 10px 7px 28px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--layer1)", color: "var(--text)", fontSize: 11, boxSizing: "border-box" }}
                />
                {notifSearch && <button onClick={() => setNotifSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--dim)", cursor: "pointer", fontSize: 14, lineHeight: 1 }}>×</button>}
              </div>
            </div>

            {/* ── List ── */}
            <div style={{ overflowY: "auto", padding: "0 10px 10px", flex: 1 }}>
              {visible.length === 0 ? (
                <div style={{ fontSize: 11, color: "var(--dim)", padding: "32px 16px", textAlign: "center" }}>
                  {notifSearch ? `No alerts matching "${notifSearch}"` : notifFilter !== "all" ? `No ${notifFilter} alerts` : "No alerts yet — autopilot scans every 5 min"}
                </div>
              ) : (
                <>
                  {pinned.length > 0 && (
                    <>
                      <SectionLabel label="Pinned" count={pinned.length} />
                      {pinned.map((n, i) => <NotifRow key={n.id || n._id || i} n={n} i={i} />)}
                    </>
                  )}
                  {todayItems.length > 0 && (
                    <>
                      <SectionLabel label="Today" count={todayItems.length} />
                      {todayItems.map((n, i) => <NotifRow key={n.id || n._id || i} n={n} i={i} />)}
                    </>
                  )}
                  {earlierItems.length > 0 && (
                    <>
                      <SectionLabel label="Earlier" count={earlierItems.length} />
                      {earlierItems.map((n, i) => <NotifRow key={n.id || n._id || i} n={n} i={i} />)}
                    </>
                  )}
                </>
              )}
            </div>

            {/* ── Footer ── */}
            <div style={{ padding: "8px 16px", borderTop: "1px solid var(--border)", flexShrink: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 9, color: "var(--dim)" }}>{visible.length} of {allNotifs.length} alerts · {dismissedNotifs.size} dismissed</span>
              <button onClick={async () => {
                setNotifRefreshing(true);
                try {
                  await axios.post(`${API}/notifications/generate`);
                  const r = await axios.get(`${API}/preload`);
                  if (r.data?.notifications) { setNotifications(r.data.notifications); setUnreadCount(r.data.unread || 0); }
                } catch {
                  toast.error("Couldn't refresh alerts — try again.");
                } finally { setNotifRefreshing(false); }
              }}
                disabled={notifRefreshing}
                style={{ fontSize: 10, padding: "4px 9px", borderRadius: 6, cursor: notifRefreshing ? "default" : "pointer", border: "1px solid var(--border)", background: "var(--layer2)", color: "var(--muted)", opacity: notifRefreshing ? 0.6 : 1 }}>
                {notifRefreshing ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>
          </>
        );
      })()}
      {showNotifs && notifDetail && (
        <NotifDetailPanel notif={notifDetail} onBack={() => setNotifDetail(null)} onClose={() => { setShowNotifs(false); setNotifDetail(null); }} />
      )}

      {/* ── LEFT SIDEBAR DRAWER ── */}
      {sideDrawerOpen && (
        <div className="side-drawer-backdrop" onClick={() => setSideDrawerOpen(false)} />
      )}
      <div className={`side-drawer ${sideDrawerOpen ? "open" : ""}`}>
        <div className="side-drawer-brand">
          <div className="side-drawer-logo">ZI</div>
          <div>
            <div className="side-drawer-title">Omega ZI</div>
            <div className="side-drawer-sub">Account command center</div>
          </div>
        </div>
        <div className="side-drawer-nav">
          {tabs.map(t => (
            <button
              key={t.id}
              className={`side-drawer-item ${activeTab === t.id ? "active" : ""}`}
              onClick={() => { switchTab(t.id); setSideDrawerOpen(false); }}
              onMouseEnter={() => prewarm(TAB_PREFETCH[t.id] || [])}
            >
              {t.id === "account" && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>}
              {t.id === "hub" && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>}
              {t.id === "intel" && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4m0-4h.01"/></svg>}
              {t.id === "tea" && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8h1a4 4 0 010 8h-1"/><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>}
              {t.id === "risk" && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>}
              {t.id === "analytics" && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>}
              {t.id === "floor" && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9m14.2 0c3.9 3.9 3.9 10.3 0 14.2M7.8 16.2a6 6 0 010-8.5m8.5 0a6 6 0 010 8.5M12 12v.01"/><circle cx="12" cy="12" r="1"/></svg>}
              {t.id === "zi" && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15 9 22 9.5 17 14.5 18.5 22 12 18 5.5 22 7 14.5 2 9.5 9 9"/></svg>}
              {t.id === "settings" && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>}
              {t.id === "docs" && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>}
              <span>{t.label}</span>
            </button>
          ))}
        </div>
        <div className="side-drawer-footer">
          <button className="side-drawer-item" onClick={logout}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            <span>Sign out</span>
          </button>
        </div>
      </div>

      <div className="app-card">
      <div className="desktop-shell">
        <div className="desktop-shell-head">
          {/* Hamburger */}
          <button className="shell-hamburger" onClick={() => setSideDrawerOpen(o => !o)} aria-label="Open menu">
            <span /><span /><span />
          </button>

          <div className="desktop-shell-brand">
            <div className="desktop-shell-logo">ZI</div>
            <div>
              <div className="desktop-shell-title">Omega ZI</div>
              <div className="desktop-shell-sub">Account command center</div>
            </div>
          </div>

          <SymbolSearchBar
            value={searchTicker}
            onChange={handleSearchInput}
            onSelect={sym => { setSearchTicker(sym); setTickerView({ symbol: sym }); }}
            onSubmit={handleSearch}
          />

          <div className="desktop-shell-actions">
            <LlmStatusLight />
            <button
              className="desktop-shell-icon"
              onClick={() => { setShowNotifs(!showNotifs); setNotifDetail(null); if (unreadCount > 0) axios.post(`${API}/notifications/read`).then(() => setUnreadCount(0)).catch(() => {}); }}
              data-testid="notif-bell"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
              {unreadCount > 0 && <span className="shell-icon-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>}
            </button>

            <div className="desktop-shell-theme" data-testid="theme-switch">
              {[
                { id: "auto", label: "Auto" },
                { id: "dark", label: "Night" },
                { id: "light", label: "Day" },
              ].map(opt => (
                <button key={opt.id} className={`desktop-shell-theme-btn ${theme === opt.id ? "active" : ""}`} onClick={() => setTheme(opt.id)} type="button">
                  {opt.label}
                </button>
              ))}
            </div>

            <span className="desktop-shell-status">{theme === "auto" ? "Auto" : "Manual"} · {resolvedTheme === "light" ? "Day" : "Night"}</span>
            <span className={`shell-regime shell-regime-${regimeTone}`} data-testid="regime-pill">{regimeLabel}</span>
            <button className="desktop-shell-user" onClick={logout} data-testid="user-menu">{(user?.name?.split(" ") || []).map(w => w[0]).join("").slice(0,2).toUpperCase() || "ZI"}</button>
          </div>
        </div>

        {/* Slim Omega system bar — visibility controlled from Settings > Autopilot */}
        {omegaStatus && showOmegaBar && (
          <div className="omega-slim-bar" data-testid="omega-bar">
            {/* Capital tier */}
            <span className="omega-tier" data-testid="omega-tier" style={{
              background: omegaStatus.capitalTier?.id === "sovereign" ? "rgba(167,139,250,0.15)" :
                omegaStatus.capitalTier?.id === "operator" ? "rgba(56,189,248,0.15)" :
                omegaStatus.capitalTier?.id === "builder" ? "rgba(74,222,128,0.15)" : "rgba(251,191,36,0.15)",
              color: omegaStatus.capitalTier?.id === "sovereign" ? "#a78bfa" :
                omegaStatus.capitalTier?.id === "operator" ? "#38bdf8" :
                omegaStatus.capitalTier?.id === "builder" ? "#4ade80" : "#fbbf24",
            }}>
              {(omegaStatus.capitalTier?.label || "Survival").toUpperCase()}
            </span>

            <div className="omega-divider" />

            {/* Autonomy selector */}
            <div className="autonomy-btns" data-testid="autonomy-selector">
              {[
                { id: "basic", label: "Basic", minZi: 88, tip: "Zi ≥ 88 · Highest confidence bar — fewest setups qualify" },
                { id: "pro",   label: "Pro",   minZi: 78, tip: "Zi ≥ 78 · High confidence bar" },
                { id: "ultra", label: "Ultra", minZi: 72, tip: "Zi ≥ 72 · Moderate confidence bar" },
                { id: "omega", label: "Omega", minZi: 65, tip: "Zi ≥ 65 · Lowest confidence bar — most setups qualify" },
              ].map(lvl => (
                <button key={lvl.id} className={`autonomy-btn ${autonomyLevel === lvl.id ? "active" : ""}`}
                  data-testid={`autonomy-${lvl.id}`}
                  data-active={autonomyLevel === lvl.id ? "true" : "false"}
                  aria-pressed={autonomyLevel === lvl.id}
                  title={lvl.tip}
                  onClick={() => changeAutonomy(lvl.id)}
                  style={{
                    border: autonomyLevel === lvl.id ? "1px solid rgba(56,189,248,0.5)" : "1px solid rgba(255,255,255,0.07)",
                    background: autonomyLevel === lvl.id ? "rgba(56,189,248,0.12)" : "transparent",
                    color: autonomyLevel === lvl.id ? "#38bdf8" : "var(--muted)",
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 1,
                  }}>
                  <span>{lvl.label}</span>
                  <span style={{ fontSize: 8, opacity: 0.7 }}>Zi≥{lvl.minZi}</span>
                </button>
              ))}
            </div>

            <div className="omega-divider" />

            <span className="omega-stat">Gate: <span>Zi ≥ {omegaStatus.autonomyConfig?.min_zi || 78}</span></span>
            <span className="omega-stat">Risk: <span>{omegaStatus.capitalTier?.max_risk_pct || 3}%</span>/trade</span>
            <span className="omega-stat">Slots: <span>{omegaStatus.capitalTier?.max_positions || 3}</span></span>

            <div className="omega-divider" />

            {/* Drift + recovery badges */}
            {omegaStatus.drift?.severity && omegaStatus.drift.severity !== "none" && (
              <span data-testid="drift-indicator" style={{ padding: "2px 6px", borderRadius: 4, fontSize: 9, fontWeight: 700,
                background: omegaStatus.drift.severity === "severe" ? "rgba(248,113,113,0.2)" : "rgba(251,191,36,0.15)",
                color: omegaStatus.drift.severity === "severe" ? "#f87171" : "#fbbf24" }}>
                DRIFT·{omegaStatus.drift.severity.toUpperCase()}
              </span>
            )}
            {omegaStatus.recoveryMode && (
              <span data-testid="recovery-indicator" style={{ padding: "2px 6px", borderRadius: 4, fontSize: 9, fontWeight: 700, background: "rgba(251,191,36,0.12)", color: "#fbbf24" }}>RECOVERY</span>
            )}

            {/* Kill switch status */}
            <span data-testid="killswitch-status">
              {omegaStatus.killSwitches?.anyFired
                ? <span style={{ padding: "2px 6px", borderRadius: 4, background: "rgba(248,113,113,0.2)", color: "#f87171", fontSize: 9, fontWeight: 700 }}>KS ACTIVE</span>
                : <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 9, color: "#4ade80" }}><span style={{ width: 5, height: 5, borderRadius: "50%", background: "#4ade80" }} /> 5/5 OK</span>
              }
            </span>

            <button data-testid="manual-killswitch" onClick={toggleManualKillSwitch}
              style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                border: "1px solid rgba(248,113,113,0.3)", background: "transparent",
                color: omegaStatus.killSwitches?.switches?.find(s => s.switch === 5)?.fired ? "#f87171" : "var(--dim)" }}>
              {omegaStatus.killSwitches?.switches?.find(s => s.switch === 5)?.fired ? "RESUME" : "HALT"}
            </button>

            <span style={{ marginLeft: "auto", fontSize: 9, color: "rgba(56,189,248,0.4)", fontWeight: 700, letterSpacing: 0.8 }}>OMEGA 6.0</span>
          </div>
        )}
      </div>

      {/* ═══ HERO KPI STRIP ═══ */}
      {!tickerView && activeTab !== "account" && activeTab !== "floor" && (() => {
        const pnl = globalDailyPnl?.totalDayPnl;
        const pnlPct = globalDailyPnl?.dayPnlPct;
        const equity = portfolio?.totalEquity;
        const bp = portfolio?.buyingPower;
        const optBp = portfolio?.optionsBuyingPower;
        const pnlPos = pnl == null ? null : pnl >= 0;
        const regimeLabel = regime ? (regime.regimeLabel || regime.regime || "").replace(/_/g, " ") : null;

        const insight = (() => {
          if (pnl != null && equity != null) {
            const dir = pnl >= 0 ? "up" : "down";
            const amt = Math.abs(pnl).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            const pct = Math.abs(pnlPct || 0).toFixed(1);
            return `Your equity is ${dir} $${amt} (${pct}%) today.${regimeLabel ? ` ${regimeLabel} regime active.` : ""}`;
          }
          return regimeLabel ? `${regimeLabel} regime — AI scanning options flow every 30s.` : "AI scanning options flow…";
        })();

        const KpiMoney = ({ value, colorize }) => {
          if (value == null) return <div className="kpi-money"><span className="kpi-int" style={{ color: "var(--dim)" }}>—</span></div>;
          const n = Number(value);
          const neg = n < 0;
          const formatted = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const [whole, dec] = formatted.split(".");
          const col = colorize ? (neg ? "var(--red)" : "var(--green)") : "var(--text)";
          return (
            <div className="kpi-money">
              <span className="kpi-curr" style={{ color: colorize ? col : "var(--muted)" }}>
                {colorize && !neg ? "+" : colorize && neg ? "-" : ""}$
              </span>
              <span className="kpi-int" style={{ color: col }}>{whole}</span>
              <span className="kpi-dec" style={{ color: colorize ? col : "var(--muted)" }}>.{dec}</span>
            </div>
          );
        };

        return (
          <div className="hero-strip">
            {/* Total Equity */}
            <div className="kpi-card">
              <div className="kpi-label">Total Equity</div>
              <KpiMoney value={equity} colorize={false} />
              <div className="kpi-sub">
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ade80", display: "inline-block", animation: "pulse 2s infinite", flexShrink: 0 }} />
                Live balance
              </div>
            </div>

            {/* Day P&L */}
            <div className="kpi-card" style={{
              borderColor: pnlPos === true ? "rgba(74,222,128,0.2)" : pnlPos === false ? "rgba(248,113,113,0.2)" : undefined,
              background: pnlPos === true ? "rgba(74,222,128,0.03)" : pnlPos === false ? "rgba(248,113,113,0.03)" : undefined,
            }}>
              <div className="kpi-label">Day P&L</div>
              <KpiMoney value={pnl} colorize={true} />
              <div className="kpi-sub">
                {pnlPct != null && (
                  <span className={`kpi-badge ${pnlPos ? "pos" : "neg"}`}>
                    {pnlPos ? "▲" : "▼"} {Math.abs(pnlPct).toFixed(1)}%
                  </span>
                )}
                <span>today</span>
              </div>
            </div>

            {/* Buying Power */}
            <div className="kpi-card">
              <div className="kpi-label">Buying Power</div>
              <KpiMoney value={bp} colorize={false} />
              <div className="kpi-sub">
                {optBp != null && <span>Options BP: {optBp != null ? "$" + Number(optBp).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : "—"}</span>}
              </div>
            </div>

            {/* AI Insights */}
            <div className="kpi-card kpi-ai">
              <div className="kpi-label">AI Insights</div>
              <div className="kpi-ai-body">
                <div className="kpi-ai-text">{insight}</div>
                <div className="kpi-ai-footer">
                  <div className="kpi-ai-badge">
                    <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#60a5fa", animation: "pulse 2s infinite" }} />
                    Omega ZI · Live
                  </div>
                  <button className="kpi-ai-arrow" onClick={() => switchTab("intel")} title="Go to Intelligence">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Account Tab — full bleed, no pad wrapper */}
      {!tickerView && activeTab === "account" && <ErrorBoundary label="Account tab crashed"><AccountTab quoteData={quoteData} regime={regime} omegaStatus={omegaStatus} omegaDispatch={omegaDispatch} portfolio={portfolio} portfolioStale={portfolioStale} refreshPortfolio={refreshHero} /></ErrorBoundary>}

      {/* Tab Content */}
      <div className="pad" style={activeTab === "account" || activeTab === "floor" ? { padding: 0 } : undefined}>
        {tickerView ? (
          <>
            <TickerCard key={tickerView.symbol} symbol={tickerView.symbol} onClose={() => { setTickerView(null); setSearchTicker(""); }} />
            {/* iter 144 — Canon: depth explainer + linkage map for the active ticker */}
            <div style={{ marginTop: 14, display: "grid", gap: 12, maxWidth: 980, margin: "14px auto 0" }}>
              <DepthExplainer topic={`Outlook for ${tickerView.symbol}`} symbol={tickerView.symbol} defaultDepth="brief" />
              <LinkageMapCard symbol={tickerView.symbol} />
            </div>
          </>
        ) : <>
          {activeTab === "account" && null}
          {activeTab === "hub" && (
            <ErrorBoundary label="Trading Hub crashed">
              <TradingHubTab apMode={apMode} setApMode={setApMode} ziThreshold={ziThreshold} setZiThreshold={setZiThreshold} deps={{ AutopilotPanel, LiveScannerFeed, UniversalScanner, OptionAnalysisModal, FlowPanel, StrategyBuilder, QuickTradeModal, TrailStopModal, AiExitVerdictModal, OmegaAIPicksPanel, setTickerView }} />
            </ErrorBoundary>
          )}
          {activeTab === "intel" && <ErrorBoundary label="Intelligence tab crashed"><IntelTab /></ErrorBoundary>}
          {activeTab === "tea" && <ErrorBoundary label="Omega Tea crashed"><OmegaTea initialTicker={teaTicker} onTickerHandled={() => setTeaTicker(null)} /></ErrorBoundary>}
          {activeTab === "risk" && <ErrorBoundary label="Risk Dashboard crashed"><RiskTab /></ErrorBoundary>}
          {activeTab === "analytics" && <ErrorBoundary label="Analytics crashed"><AnalyticsTab omegaDispatch={omegaDispatch} initialSubTab={enhanceTargetSubTab} onSubTabConsumed={() => setEnhanceTargetSubTab(null)} /></ErrorBoundary>}
          {activeTab === "floor" && <ErrorBoundary label="Trading Floor crashed"><Suspense fallback={<LazyScreenFallback />}><FloorChat switchTab={switchTab} /></Suspense></ErrorBoundary>}
          {activeTab === "zi" && <ErrorBoundary label="Zi Portfolios crashed"><OmegaZiPortfolioCards /></ErrorBoundary>}
          {activeTab === "btc15" && <ErrorBoundary label="BTC 15 crashed"><Btc15Tab /></ErrorBoundary>}
          {activeTab === "settings" && <ErrorBoundary label="Settings crashed"><SettingsTab health={health} theme={theme} setTheme={setTheme} mobileUiMode={mobileUiMode} setMobileUiMode={setMobileUiMode} /></ErrorBoundary>}
          {activeTab === "docs" && <ErrorBoundary label="Docs tab crashed"><DocsTab /></ErrorBoundary>}
        </>}
      </div>
      {activeTab === "account" && (
        <div className="app-footer-bar">
          Use the left drawer for navigation and theme. Click the chart dots to change the event detail panel.
        </div>
      )}
      </div>{/* end .app-card */}
      <ActiveOrdersStrip />

      {isOffline && (
        <div data-testid="offline-banner" style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 10003, background: "#b45309", color: "#fff", padding: "8px 16px", textAlign: "center", fontSize: 12, fontWeight: 600, boxShadow: "0 2px 10px rgba(0,0,0,0.4)" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#fbbf24", animation: "pulse 1.5s infinite" }} />
            Offline · Reconnecting… · Using cached data
          </span>
        </div>
      )}
      {swUpdateReady && (
        <div
          data-testid="sw-update-toast"
          style={{
            position: "fixed", bottom: 20, left: 20, right: 20, zIndex: 10004,
            background: "linear-gradient(145deg, #1e40af, #2563eb)",
            border: "1px solid rgba(147,197,253,0.4)",
            borderRadius: 16, padding: "14px 16px",
            boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", gap: 12,
            color: "#fff", fontSize: 13,
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#93c5fd", animation: "pulse 1.5s infinite", flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>New version ready</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.8)" }}>Tap refresh to load the latest design.</div>
          </div>
          <button
            data-testid="sw-update-refresh"
            onClick={() => window.location.reload()}
            style={{
              padding: "8px 14px", borderRadius: 10, border: "none",
              background: "#fff", color: "#1e40af", fontSize: 12, fontWeight: 700, cursor: "pointer",
              flexShrink: 0,
            }}
          >Refresh</button>
          <button
            data-testid="sw-update-dismiss"
            onClick={() => setSwUpdateReady(false)}
            style={{
              width: 28, height: 28, borderRadius: 14, border: "none",
              background: "rgba(255,255,255,0.12)", color: "#fff", fontSize: 16, cursor: "pointer",
              flexShrink: 0,
            }}
          >&times;</button>
        </div>
      )}
    </div>
  );
}


// ─── Open Position Modal ──────────────────────────────────────────────────────

// ─── Email Alerts Row — Toggle + test-send ───────────────────────────────────
// EmailAlertsRow + EnableBrowserNotifButton extracted to ./components/NotifRows.js


// ─── Account Life Chart (Comprehensive Equity + Events + Forecast) ────────────
function AccountLifeChart({ dailyPnl, accountInsight, regime, omegaStatus, onMetricSelect }) {
  const [data, setData] = useState(null);
  const [selectedDot, setSelectedDot] = useState(null);
  const [aiNarrative, setAiNarrative] = useState("");
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const chartRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/account/life-chart`).then(r => setData(r.data)).catch(() => {});
  }, []);

  // Generate AI narrative when data loads
  useEffect(() => {
    if (!data || narrativeLoading || aiNarrative) return;
    setNarrativeLoading(true);
    const { timeline, events, currentEquity, positions, forecast } = data;
    const wins = events.filter(e => e.type === "trade" && e.amount > 0).length;
    const losses = events.filter(e => e.type === "trade" && e.amount < 0).length;
    const trades = events.filter(e => e.type === "trade");
    const totalPnl = trades.reduce((s, e) => s + (e.amount || 0), 0);
    const biggestWin = trades.length > 0 ? trades.reduce((max, e) => e.amount > max.amount ? e : max, trades[0]) : null;
    const biggestLoss = trades.length > 0 ? trades.reduce((min, e) => e.amount < min.amount ? e : min, trades[0]) : null;
    const forecastEnd = forecast?.length > 0 ? forecast[forecast.length - 1].equity : currentEquity;
    const forecastDir = forecastEnd > currentEquity ? "upward" : forecastEnd < currentEquity ? "downward" : "flat";

    const prompt = `Analyze this trading account in 3 concise sentences:
Equity: $${currentEquity.toFixed(2)}, ${trades.length} trades (${wins}W/${losses}L), P&L: $${totalPnl.toFixed(2)}
${biggestWin ? `Best: ${biggestWin.symbol} +$${biggestWin.amount.toFixed(2)}` : ""}
${biggestLoss ? `Worst: ${biggestLoss.symbol} $${biggestLoss.amount.toFixed(2)}` : ""}
Open: ${positions.map(p => p.symbol).join(", ")}
30-day forecast: ${forecastDir} to $${forecastEnd.toFixed(2)}
Give: 1) Current state assessment 2) Key pattern 3) Forward outlook. Keep under 80 words.`;

    axios.post(`${API}/zi/chat`, { message: prompt, sessionId: `eq-insight-${Date.now()}` })
      .then(r => setAiNarrative(r.data?.response || ""))
      .catch(() => {
        // Fallback narrative from data
        let narrative = `Account at $${currentEquity.toFixed(2)} with ${trades.length} completed trades (${wins}W/${losses}L). `;
        if (totalPnl > 0) narrative += `Net profitable at +$${totalPnl.toFixed(2)}. `;
        else if (totalPnl < 0) narrative += `Net loss of $${Math.abs(totalPnl).toFixed(2)} — recovery mode recommended. `;
        if (biggestWin) narrative += `Best trade: ${biggestWin.symbol} (+$${biggestWin.amount.toFixed(2)}). `;
        narrative += `30-day projection: ${forecastDir} to $${forecastEnd.toFixed(2)}.`;
        setAiNarrative(narrative);
      })
      .finally(() => setNarrativeLoading(false));
  }, [data, aiNarrative, narrativeLoading]);

  if (!data || !data.timeline?.length) {
    return (
      <div className="card" style={{ borderColor: "rgba(167,139,250,0.2)" }}>
        <div className="sh">Total Equity & Open Positions</div>
        <div style={{ textAlign: "center", padding: 20, color: "var(--dim)", fontSize: 12 }}>
          <span className="spin" style={{ marginRight: 8 }} /> Loading account life data...
        </div>
      </div>
    );
  }

  const { timeline, events, positions, forecast, currentEquity } = data;
  const previousClose = timeline.length > 1 ? Number(timeline[timeline.length - 2]?.equity || currentEquity) : Number(currentEquity || 0);
  const equityIsPositive = currentEquity >= previousClose;
  const totalDayPnl = dailyPnl?.totalDayPnl ?? (currentEquity - previousClose);
  const dayPnlPct = dailyPnl?.dayPnlPct ?? (previousClose > 0 ? ((currentEquity - previousClose) / previousClose) * 100 : 0);
  const tradeEvents = events.filter(e => e.type === "trade");
  const wins = tradeEvents.filter(e => e.amount > 0).length;
  const losses = tradeEvents.filter(e => e.amount < 0).length;
  const winRate = accountInsight?.winRate ?? (tradeEvents.length > 0 ? (wins / tradeEvents.length) * 100 : 0);
  const totalTradePnl = accountInsight?.totalPnl ?? tradeEvents.reduce((sum, ev) => sum + (ev.amount || 0), 0);
  const forecastEnd = forecast?.length > 0 ? forecast[forecast.length - 1].equity : currentEquity;
  const forecastUp = forecastEnd >= currentEquity;

  let eqLabels = timeline.map(t => t.date);
  let eqData = timeline.map(t => t.equity);
  const today = new Date().toISOString().slice(0, 10);
  if (!eqLabels.includes(today)) { eqLabels.push(today); eqData.push(currentEquity); }
  else { eqData[eqLabels.indexOf(today)] = currentEquity; }

  const ma20 = eqData.map((_, i) => {
    const start = Math.max(0, i - 19);
    const slice = eqData.slice(start, i + 1);
    if (slice.length < 2) return null;
    return slice.reduce((sum, val) => sum + Number(val || 0), 0) / slice.length;
  });

  // Forecast
  const forecastLabels = forecast.map(f => `+${f.day}d`);
  const forecastData = [...eqData.map(() => null)];
  forecastData[forecastData.length - 1] = eqData[eqData.length - 1];
  const forecastLine = forecast.map(f => f.equity);
  const baselineLine = [...eqData.map(() => previousClose), ...forecastLabels.map(() => previousClose)];

  // Separate event dots by type for distinct colors/shapes
  const gainDots = eqLabels.map(() => null);
  const lossDots = eqLabels.map(() => null);
  const depositDots = eqLabels.map(() => null);
  const spikeDots = eqLabels.map(() => null);
  events.forEach(ev => {
    const idx = eqLabels.indexOf(ev.date);
    if (idx < 0) return;
    if (ev.type === "trade" && (ev.amount || 0) >= 0) gainDots[idx] = eqData[idx];
    else if (ev.type === "trade") lossDots[idx] = eqData[idx];
    else if (ev.type === "deposit") depositDots[idx] = eqData[idx];
    else spikeDots[idx] = eqData[idx]; // spike, withdrawal, etc.
  });

  const allLabels = [...eqLabels, ...forecastLabels];
  const nullForecast = forecastLabels.map(() => null);

  const chartData = {
    labels: allLabels,
    datasets: [
      { label: "Yesterday Close", data: baselineLine, borderColor: "rgba(120,130,140,0.65)", borderDash: [6, 6], pointRadius: 0, borderWidth: 1.1, tension: 0, fill: false, order: 1 },
      { label: "MA20", data: [...ma20, ...nullForecast], borderColor: "rgba(167,139,250,0.95)", pointRadius: 0, borderWidth: 1.6, tension: 0.35, fill: false, order: 2 },
      { label: "Total Equity", data: [...eqData, ...nullForecast], borderColor: equityIsPositive ? "#22c55e" : "#ef4444", backgroundColor: equityIsPositive ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)", fill: true, pointRadius: 0, borderWidth: 4, tension: 0.35, order: 5 },
      { label: "Zi Forecast", data: [...forecastData, ...forecastLine], borderColor: forecastUp ? "rgba(34,197,94,0.95)" : "rgba(239,68,68,0.95)", borderDash: [4, 4], backgroundColor: forecastUp ? "rgba(34,197,94,0.04)" : "rgba(239,68,68,0.04)", fill: false, pointRadius: 0, borderWidth: 2, tension: 0.35, order: 6 },
      { label: "Positive Event", data: [...gainDots, ...nullForecast], borderColor: "transparent", backgroundColor: gainDots.map(v => v !== null ? "#22c55e" : "transparent"), pointRadius: gainDots.map(v => v !== null ? 7 : 0), pointHoverRadius: gainDots.map(v => v !== null ? 11 : 0), pointBorderWidth: 2, pointBorderColor: gainDots.map(v => v !== null ? "#14532d" : "transparent"), pointStyle: "circle", showLine: false, fill: false, order: 7 },
      { label: "Negative Event", data: [...lossDots, ...nullForecast], borderColor: "transparent", backgroundColor: lossDots.map(v => v !== null ? "#ef4444" : "transparent"), pointRadius: lossDots.map(v => v !== null ? 7 : 0), pointHoverRadius: lossDots.map(v => v !== null ? 11 : 0), pointBorderWidth: 2, pointBorderColor: lossDots.map(v => v !== null ? "#7f1d1d" : "transparent"), pointStyle: "circle", showLine: false, fill: false, order: 7 },
      { label: "Deposit", data: [...depositDots, ...nullForecast], borderColor: "transparent", backgroundColor: depositDots.map(v => v !== null ? "#60a5fa" : "transparent"), pointRadius: depositDots.map(v => v !== null ? 8 : 0), pointHoverRadius: depositDots.map(v => v !== null ? 11 : 0), pointBorderWidth: 2, pointBorderColor: depositDots.map(v => v !== null ? "#1d4ed8" : "transparent"), pointStyle: "triangle", showLine: false, fill: false, order: 7 },
      { label: "Spike / Loss", data: [...spikeDots, ...nullForecast], borderColor: "transparent", backgroundColor: spikeDots.map(v => v !== null ? "#f87171" : "transparent"), pointRadius: spikeDots.map(v => v !== null ? 8 : 0), pointHoverRadius: spikeDots.map(v => v !== null ? 12 : 0), pointBorderWidth: 2, pointBorderColor: spikeDots.map(v => v !== null ? "#f87171" : "transparent"), pointStyle: "crossRot", showLine: false, fill: false, order: 3 },
    ],
  };

  const chartOpts = {
    responsive: true, maintainAspectRatio: false,
    onClick: (evt, elements) => {
      if (elements.length > 0 && [4, 5, 6, 7].includes(elements[0].datasetIndex)) {
        const idx = elements[0].index;
        const date = eqLabels[idx];
        const ev = events.find(e => e.date === date);
        if (ev) setSelectedDot(selectedDot?.date === date ? null : ev);
      }
    },
    plugins: {
      legend: {
        display: true,
        labels: {
          color: "var(--muted)", boxWidth: 12, font: { size: 9 }, padding: 12,
          usePointStyle: true,
          filter: (item) => !["Account Equity"].includes(item.text),
        },
      },
      tooltip: {
        mode: "index", intersect: false,
        backgroundColor: "var(--surface)", titleColor: "var(--text)", bodyColor: "var(--muted)",
        callbacks: {
          label: (ctx) => {
            if ([4, 5, 6, 7].includes(ctx.datasetIndex) && ctx.raw !== null) {
              const ev = events.find(e => eqLabels[ctx.dataIndex] === e.date);
              return ev ? `${ev.type.toUpperCase()}: ${ev.description} (${ev.amount >= 0 ? "+" : ""}$${ev.amount.toFixed(2)})` : "";
            }
            if (ctx.datasetIndex <= 3 && ctx.raw !== null) return `$${Number(ctx.raw).toFixed(2)}`;
            return "";
          },
        },
      },
    },
    scales: {
      x: { ticks: { color: "var(--dim)", font: { size: 8 }, maxTicksLimit: 10 }, grid: { color: "var(--grid)" } },
      y: { ticks: { color: "var(--dim)", font: { size: 9 }, callback: v => "$" + v }, grid: { color: "var(--grid)" }, min: 0 },
    },
  };

  const eventColors = { trade: { bg: "rgba(250,204,21,0.08)", border: "#facc15", label: "TRADE" }, deposit: { bg: "rgba(74,222,128,0.08)", border: "#4ade80", label: "DEPOSIT" }, spike: { bg: "rgba(248,113,113,0.08)", border: "#f87171", label: "SPIKE / LOSS" } };
  const heroStats = [
    { label: "Today's P&L", value: fmt$(totalDayPnl), tone: totalDayPnl >= 0 ? "pos" : "neg", sub: `${totalDayPnl >= 0 ? "+" : ""}${fmtN(dayPnlPct, 2)}%` },
    { label: "Total Equity", value: fmt$(currentEquity), tone: equityIsPositive ? "pos" : "neg", sub: "Live account value" },
    { label: "Open Positions", value: String(positions.length), tone: "neutral", sub: `${dailyPnl?.autopilotOpen || 0} autopilot` },
    { label: "Account Performance", value: `${fmtN(winRate, 1)}%`, tone: winRate >= 50 ? "pos" : "neg", sub: `${tradeEvents.length} closed trades` },
  ];

  return (
    <div className="acc-hero-card" data-testid="life-chart">
      <div className="acc-hero-head">
        <div>
          <div className="acc-hero-kicker">Account Profile · Live</div>
          <div className="acc-hero-title">Total Equity &amp; Open Positions</div>
          <div className="acc-hero-note">Account lifetime — click dots for event details · Equity + Zi AI forecast + event markers</div>
        </div>
        <div className="acc-hero-balance-stack">
          <div className={`acc-hero-balance-big ${equityIsPositive ? "pos" : "neg"}`}>{fmt$(currentEquity)}</div>
          <div className="acc-hero-balance-sub">
            <span className={equityIsPositive ? "pos" : "neg"}>{equityIsPositive ? "+" : ""}{fmt$(currentEquity - previousClose)}</span>
            <span>{events.length} events</span>
            <span>{positions.length} open</span>
          </div>
        </div>
      </div>

      <div className="acc-hero-stat-grid">
        {heroStats.map(stat => (
          <button
            key={stat.label}
            type="button"
            className={`acc-hero-stat acc-tone-${stat.tone}`}
            onClick={() => {
              if (stat.label === "Today's P&L") onMetricSelect?.("pnl");
              if (stat.label === "Total Equity") onMetricSelect?.("equity");
            }}
          >
            <div className="acc-hero-stat-label">{stat.label}</div>
            <div className="acc-hero-stat-value">{stat.value}</div>
            <div className="acc-hero-stat-sub">{stat.sub}</div>
          </button>
        ))}
      </div>

      {positions.length > 0 && (
        <div className="acc-hero-chip-row">
          {positions.slice(0, 8).map(p => (
            <span key={p.rawSymbol || p.symbol} className="acc-hero-chip">
              <span className="acc-hero-chip-sym">{p.symbol}</span>
              <span>{fmt$(p.value)}</span>
              <span className={p.gainPct >= 0 ? "pos" : "neg"}>{fmtPct(p.gainPct)}</span>
            </span>
          ))}
        </div>
      )}

      <div className="acc-hero-chart-shell">
        <div className="acc-hero-chart-topline">
          <span>{regime?.regime ? `${String(regime.regime).replace(/_/g, " ")} regime` : "Regime loading"}</span>
          <span>{omegaStatus?.capitalTier?.label ? `${omegaStatus.capitalTier.label} tier` : "Tier loading"}</span>
          <span>{forecastUp ? "Zi forecast upward" : "Zi forecast defensive"}</span>
        </div>
        <div className="acc-hero-chart-canvas"><Line ref={chartRef} data={chartData} options={chartOpts} /></div>
      </div>

      {/* Event Detail Card (when dot clicked) */}
      {selectedDot && (
        <div className="acc-hero-event-card" style={{ background: (eventColors[selectedDot.type] || eventColors.trade).bg, borderColor: `${(eventColors[selectedDot.type] || eventColors.trade).border}30` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: (eventColors[selectedDot.type] || eventColors.trade).border }}>{selectedDot.date}</span>
              <span className={`tag ${selectedDot.type === "deposit" ? "t-bull" : selectedDot.type === "trade" ? "t-neu" : "t-bear"}`} style={{ fontSize: 8 }}>{(eventColors[selectedDot.type] || eventColors.trade).label}</span>
            </div>
            <button onClick={() => setSelectedDot(null)} style={{ background: "none", border: "none", color: "var(--dim)", cursor: "pointer", fontSize: 16 }}>&times;</button>
          </div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6 }}>{selectedDot.description}</div>
          <div style={{ display: "flex", gap: 14, fontSize: 10, color: "var(--muted)", marginBottom: 8, flexWrap: "wrap" }}>
            {selectedDot.symbol && <span>Symbol: <strong>{selectedDot.symbol}</strong></span>}
            <span>Amount: <strong className={selectedDot.amount >= 0 ? "pos" : "neg"}>{selectedDot.amount >= 0 ? "+" : ""}{fmt$(selectedDot.amount)}</strong></span>
            <span>Equity: <strong>{fmt$(selectedDot.equity_at)}</strong></span>
            {selectedDot.changePct != null && <span>Change: <strong className={selectedDot.changePct >= 0 ? "pos" : "neg"}>{selectedDot.changePct >= 0 ? "+" : ""}{fmtPct(selectedDot.changePct)}</strong></span>}
          </div>
          <div style={{ padding: 8, background: "rgba(96,165,250,0.06)", borderRadius: 6, fontSize: 10 }}>
            <span style={{ fontWeight: 600, color: "#a78bfa" }}>Omega ZI AI:</span>{" "}
            <span style={{ color: "var(--text)" }}>
              {selectedDot.type === "deposit" ? "Capital infusion strengthens buying power. Deploy into high-PoP GEM-class setups during favorable regime." :
               selectedDot.type === "trade" && selectedDot.amount >= 0 ? `Profitable exit +${fmt$(selectedDot.amount)} on ${selectedDot.symbol}. Bayesian priors updated — ${selectedDot.amount > 50 ? "strong signal, increasing conviction on similar setups." : "modest gain, engine recalibrating entry thresholds."}` :
               selectedDot.type === "trade" && selectedDot.amount < 0 ? `Loss of ${fmt$(Math.abs(selectedDot.amount))} on ${selectedDot.symbol}. Risk Gate triggered protective exit. Engine learning: reducing position size for this setup class.` :
               selectedDot.type === "spike" ? `Significant movement of ${selectedDot.changePct >= 0 ? "+" : ""}${fmtPct(selectedDot.changePct)}. ${(selectedDot.changePct || 0) > 0 ? "Strong appreciation — consider trailing stops." : "Sharp decline — Drawdown Kill Switch may activate."}` :
               "Event tracked for pattern analysis."}
            </span>
          </div>
        </div>
      )}

      {/* ═══ OMEGA ZI AI INSIGHT — Persistent narrative ═══ */}
      <div className="acc-hero-ai-card" data-testid="equity-ai-insight">
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#a78bfa" }}>OMEGA ZI AI INSIGHT</span>
          <span className="tag" style={{ fontSize: 7, background: "rgba(167,139,250,0.15)", color: "#a78bfa" }}>ANALYSIS</span>
        </div>
        {narrativeLoading ? (
          <div style={{ fontSize: 11, color: "var(--dim)", padding: "4px 0" }}><span className="spin" style={{ marginRight: 6 }} />Analyzing equity trajectory...</div>
        ) : (
          <div style={{ fontSize: 11, color: "var(--text)", lineHeight: 1.6 }}
            dangerouslySetInnerHTML={{ __html: formatAiText((accountInsight?.insight && `${accountInsight.insight} ${aiNarrative}`) || aiNarrative || "") }} />
        )}
        <div className="acc-hero-ai-foot">
          <span className={totalTradePnl >= 0 ? "pos" : "neg"}>{totalTradePnl >= 0 ? "+" : ""}{fmt$(totalTradePnl)} realized</span>
          <span>{wins} wins / {losses} losses</span>
          <span>{forecastUp ? `Forecast to ${fmt$(forecastEnd)}` : `Risk line near ${fmt$(forecastEnd)}`}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Ultra-Comprehensive Notification Detail Panel ────────────────────────────
function NotifDetailPanel({ notif, onBack, onClose }) {
  const [enriched, setEnriched] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Deep-dive AI summary + payoff
  const [dive, setDive] = useState(null);
  const [diveChart, setDiveChart] = useState(null);

  const ticker = notif.ticker || notif.data?.ticker || "";
  const symbol = notif.data?.symbol || notif.symbol || "";
  const catColors = { scan: "#60a5fa", trade: "#4ade80", risk: "#f87171", tea: "#fbbf24", alert: "#a78bfa" };
  const catColor = catColors[notif.category] || "#60a5fa";

  useEffect(() => {
    if (!ticker && !symbol) { setLoading(false); return; }
    axios.get(`${API}/notification/enrich?ticker=${encodeURIComponent(ticker)}&symbol=${encodeURIComponent(symbol)}`).then(r => {
      setEnriched(r.data);
      const bars = r.data?.chartBars || [];
      if (bars.length > 1) {
        const closes = bars.map(b => b.c);
        setChartData({
          labels: bars.map(b => new Date(b.t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })),
          datasets: [{ data: closes, borderColor: closes[closes.length-1] >= closes[0] ? "#4ade80" : "#f87171", backgroundColor: closes[closes.length-1] >= closes[0] ? "rgba(74,222,128,0.08)" : "rgba(248,113,113,0.08)", fill: true, pointRadius: 0, borderWidth: 1.5, tension: 0.2 }],
        });
      }
    }).catch(() => {}).finally(() => setLoading(false));

    // Parallel: Omega AI deep-dive (identical endpoint as mobile)
    const nid = notif.id || notif._id;
    if (nid) {
      axios.post(`${API}/notifications/deep-dive`, { id: nid }).then(r => {
        setDive(r.data);
        if (r.data?.callPayoff?.length > 0 && r.data?.putPayoff?.length > 0) {
          setDiveChart({
            labels: r.data.callPayoff.map(p => `$${p.spot}`),
            datasets: [
              { label: "Call", data: r.data.callPayoff.map(p => p.pnl), borderColor: "#4ade80", backgroundColor: "rgba(74,222,128,0.08)", fill: true, borderWidth: 1.6, pointRadius: 0, tension: 0.1 },
              { label: "Put",  data: r.data.putPayoff.map(p => p.pnl),  borderColor: "#f87171", backgroundColor: "rgba(248,113,113,0.08)", fill: true, borderWidth: 1.6, pointRadius: 0, tension: 0.1 },
            ],
          });
        }
      }).catch(() => {});
    }
  }, [ticker, symbol]);

  const co = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { mode: "index", intersect: false } }, scales: { x: { ticks: { color: "var(--dim)", font: { size: 8 }, maxTicksLimit: 6 }, grid: { color: "var(--grid)" } }, y: { ticks: { color: "var(--dim)", font: { size: 9 } }, grid: { color: "var(--grid)" } } } };
  const grk = enriched?.greeks || {};
  const parsed = enriched?.parsed;
  const q = enriched?.quote || {};
  const oq = enriched?.optionQuote || {};

  return (
    <>
    <div style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)" }} onClick={onClose} />
    <div style={{
      position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
      width: "min(900px, calc(100vw - 32px))", height: "min(680px, calc(100vh - 48px))",
      display: "flex", flexDirection: "column",
      background: "var(--surface)", border: `1px solid ${catColor}35`,
      borderRadius: 20, zIndex: 9999,
      boxShadow: `0 32px 120px rgba(0,0,0,0.95), 0 0 0 1px ${catColor}15`,
    }} className="notif-panel" data-testid="notif-detail">

      {/* ── Header ── */}
      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${catColor}20`, display: "flex", alignItems: "center", justifyContent: "space-between", background: `${catColor}06`, borderRadius: "20px 20px 0 0", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 5, background: "var(--layer2)", border: "1px solid var(--border)", cursor: "pointer", color: "var(--muted)", fontSize: 12, padding: "4px 10px", borderRadius: 7 }} data-testid="notif-back">← Back</button>
          <div style={{ width: 1, height: 18, background: "var(--border)" }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>{notif.title || "Notification Detail"}</div>
            <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 2 }}>
              <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 7px", borderRadius: 4, background: `${catColor}20`, color: catColor }}>{(notif.category || "info").toUpperCase()}</span>
              {ticker && <span style={{ fontSize: 10, fontWeight: 700, color: catColor }}>{ticker}</span>}
              {notif.ziScore && <span style={{ fontSize: 10, fontWeight: 800, color: notif.ziScore >= 68 ? "#4ade80" : notif.ziScore >= 53 ? "#60a5fa" : "#fbbf24" }}>Zi {notif.ziScore}</span>}
              {notif.ziClass && <span className={`tag ${notif.ziClass === "ULTRA_GEM" ? "t-bull" : "t-blue"}`} style={{ fontSize: 8 }}>{notif.ziClass?.replace("_"," ")}</span>}
              <span style={{ fontSize: 9, color: "var(--dim)" }}>{notif.createdAt ? new Date(notif.createdAt).toLocaleString() : ""}</span>
            </div>
          </div>
        </div>
        <button onClick={onClose} style={{ background: "var(--layer2)", border: "1px solid var(--border)", color: "var(--text)", width: 32, height: 32, borderRadius: 9, cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="notif-detail-close">×</button>
      </div>

      {/* ── Two-column body ── */}
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>

        {/* LEFT — Chart, quote, data */}
        <div style={{ flex: "0 0 55%", borderRight: `1px solid ${catColor}15`, overflowY: "auto", padding: 20 }}>

          {/* Ticker hero */}
          {(ticker || notif.ziScore) && (
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16, padding: "12px 14px", borderRadius: 12, background: `${catColor}08`, border: `1px solid ${catColor}18` }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: `${catColor}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 900, color: catColor, flexShrink: 0 }}>
                {ticker?.slice(0, 2) || "Ω"}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: "var(--text)", lineHeight: 1 }}>{ticker || "Alert"}</div>
                {parsed && <div style={{ fontSize: 11, color: "#60a5fa", marginTop: 3 }}>${parsed.strike} {parsed.side} — {parsed.itm ? "ITM" : "OTM"} — {parsed.dte}d to exp</div>}
              </div>
              {notif.ziScore && (
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 36, fontWeight: 900, lineHeight: 1, color: notif.ziScore >= 68 ? "#4ade80" : notif.ziScore >= 53 ? "#60a5fa" : "#fbbf24" }}>{notif.ziScore}</div>
                  <div style={{ fontSize: 9, color: "var(--dim)", marginTop: 2 }}>Zi Score</div>
                </div>
              )}
            </div>
          )}

          {/* Live chart */}
          {loading ? (
            <div style={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--dim)", fontSize: 11, gap: 8 }}>
              <span className="spin" />Loading live data...
            </div>
          ) : chartData ? (
            <div style={{ height: 180, marginBottom: 14, borderRadius: 10, overflow: "hidden", background: "var(--layer2)", padding: "10px 10px 6px" }}>
              <Line data={chartData} options={co} />
            </div>
          ) : null}

          {/* Live quote bar */}
          {!loading && q.price > 0 && (
            <div style={{ display: "flex", gap: 0, marginBottom: 14, borderRadius: 10, overflow: "hidden", border: "1px solid rgba(56,189,248,0.15)" }}>
              {[
                { label: "Price", val: `$${fmtN(q.price, 2)}`, color: "#38bdf8" },
                { label: "Bid",   val: `$${fmtN(q.bid, 2)}`,   color: "var(--green)" },
                { label: "Ask",   val: `$${fmtN(q.ask, 2)}`,   color: "var(--red)" },
                ...(q.volume > 0 ? [{ label: "Volume", val: Number(q.volume).toLocaleString(), color: "var(--text)" }] : []),
              ].map(({ label, val, color }, i, arr) => (
                <div key={label} style={{ flex: 1, padding: "8px 10px", background: "rgba(56,189,248,0.04)", borderRight: i < arr.length - 1 ? "1px solid rgba(56,189,248,0.1)" : "none", textAlign: "center" }}>
                  <div style={{ fontSize: 8, color: "var(--dim)", marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color }}>{val}</div>
                </div>
              ))}
            </div>
          )}

          {/* Greeks */}
          {oq.bid != null && (
            <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
              {[
                { label: "Opt Bid", val: `$${fmtN(oq.bid, 2)}`, color: "var(--text)" },
                { label: "Opt Ask", val: `$${fmtN(oq.ask, 2)}`, color: "var(--text)" },
                ...(grk.delta != null ? [{ label: "Delta", val: fmtN(grk.delta, 3), color: "#60a5fa" }] : []),
                ...(grk.theta != null ? [{ label: "Theta", val: fmtN(grk.theta, 4), color: "#f87171" }] : []),
                ...(grk.iv    != null ? [{ label: "IV",    val: `${fmtN(grk.iv * 100, 0)}%`, color: "#fbbf24" }] : []),
                ...(grk.gamma != null ? [{ label: "Gamma", val: fmtN(grk.gamma, 4), color: "#a78bfa" }] : []),
              ].map(({ label, val, color }) => (
                <div key={label} className="m-box" style={{ flex: 1, minWidth: 60, padding: "6px 8px", textAlign: "center" }}>
                  <div className="m-lbl" style={{ fontSize: 8 }}>{label}</div>
                  <div className="m-val" style={{ fontSize: 13, color }}>{val}</div>
                </div>
              ))}
            </div>
          )}

          {/* Category data */}
          {notif.category === "scan" && (
            <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
              {[
                { label: "Strike",  val: `$${notif.data?.strike || "—"}` },
                { label: "Premium", val: `$${notif.data?.mid?.toFixed(2) || "—"}` },
                { label: "MC PoP",  val: notif.data?.mcPoP ? `${(notif.data.mcPoP * 100).toFixed(1)}%` : "—" },
              ].map(({ label, val }) => (
                <div key={label} className="m-box" style={{ flex: 1, minWidth: 70, padding: "6px 8px", textAlign: "center" }}>
                  <div className="m-lbl" style={{ fontSize: 8 }}>{label}</div>
                  <div className="m-val" style={{ fontSize: 13 }}>{val}</div>
                </div>
              ))}
            </div>
          )}
          {notif.category === "trade" && (
            <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
              {[
                { label: "Strike", val: `$${notif.data?.strike || notif.strike || "—"}` },
                { label: "Qty",    val: notif.data?.quantity || 1 },
                { label: "Entry",  val: `$${notif.data?.mid?.toFixed(2) || notif.data?.entryPrice?.toFixed(2) || "—"}` },
              ].map(({ label, val }) => (
                <div key={label} className="m-box" style={{ flex: 1, padding: "6px 8px", textAlign: "center" }}>
                  <div className="m-lbl" style={{ fontSize: 8 }}>{label}</div>
                  <div className="m-val" style={{ fontSize: 13 }}>{val}</div>
                </div>
              ))}
            </div>
          )}
          {notif.category === "risk" && notif.data?.equity && (
            <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
              <div className="m-box" style={{ flex: 1, padding: "6px 8px", textAlign: "center" }}><div className="m-lbl" style={{ fontSize: 8 }}>Equity</div><div className="m-val" style={{ fontSize: 13 }}>${typeof notif.data.equity === "number" ? notif.data.equity.toLocaleString() : notif.data.equity}</div></div>
              <div className="m-box" style={{ flex: 1, padding: "6px 8px", textAlign: "center" }}><div className="m-lbl" style={{ fontSize: 8 }}>Threshold</div><div className="m-val" style={{ fontSize: 13 }}>${typeof notif.data.threshold === "number" ? notif.data.threshold.toLocaleString() : notif.data.threshold}</div></div>
            </div>
          )}

          {/* Message */}
          <div style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.7, padding: "10px 12px", borderRadius: 8, background: "var(--layer1)", border: "1px solid var(--border)" }}>
            {notif.message}
          </div>
        </div>

        {/* RIGHT — Omega ZI AI insight */}
        <div style={{ flex: "0 0 45%", overflowY: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>

          {/* AI insight from notif */}
          {notif.insight && (
            <div style={{ padding: 14, borderRadius: 12, background: `${catColor}08`, border: `1px solid ${catColor}18` }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: catColor, letterSpacing: 1.5, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: catColor, display: "inline-block" }} />
                OMEGA AI FORECAST
              </div>
              <div style={{ fontSize: 12, color: "var(--text)", lineHeight: 1.7 }}>{notif.insight}</div>
            </div>
          )}

          {/* Deep dive loading */}
          {!dive && loading && (
            <div style={{ padding: 14, borderRadius: 12, background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.15)", display: "flex", alignItems: "center", gap: 10, color: "var(--muted)", fontSize: 11 }}>
              <span className="spin" />Omega ZI AI deep dive loading...
            </div>
          )}

          {/* Deep dive */}
          {dive && (
            <div data-testid="desktop-deep-dive" style={{ padding: 16, borderRadius: 14, background: "linear-gradient(135deg, rgba(167,139,250,0.10), rgba(96,165,250,0.04))", border: "1px solid rgba(167,139,250,0.25)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#a78bfa", display: "inline-block" }} />
                <span style={{ fontSize: 10, fontWeight: 800, color: "#a78bfa", letterSpacing: 1.5 }}>OMEGA ZI AI · DEEP DIVE</span>
                {dive.verdict && (
                  <span style={{
                    marginLeft: "auto", fontSize: 9, fontWeight: 800, padding: "3px 9px", borderRadius: 6, letterSpacing: 1,
                    background: dive.verdict === "bullish" ? "rgba(74,222,128,0.2)" : dive.verdict === "bearish" ? "rgba(248,113,113,0.2)" : "rgba(96,165,250,0.2)",
                    color: dive.verdict === "bullish" ? "#4ade80" : dive.verdict === "bearish" ? "#f87171" : "#60a5fa",
                  }}>{dive.verdict.toUpperCase()}</span>
                )}
              </div>
              {dive.summary && <div style={{ fontSize: 12, color: "var(--text)", lineHeight: 1.75, marginBottom: 12 }}>{dive.summary}</div>}
              {dive.recommendation && (
                <div style={{ padding: "10px 12px", borderRadius: 8, background: "var(--layer2)", borderLeft: `3px solid ${dive.verdict === "bullish" ? "#4ade80" : dive.verdict === "bearish" ? "#f87171" : "#60a5fa"}` }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: "#a78bfa", letterSpacing: 1, marginBottom: 4 }}>RECOMMENDATION</div>
                  <div style={{ fontSize: 11, color: "var(--text)", lineHeight: 1.6 }}>{dive.recommendation}</div>
                </div>
              )}
            </div>
          )}

          {/* Call vs Put payoff chart */}
          {diveChart && (
            <div data-testid="desktop-deep-dive-chart" style={{ padding: 14, borderRadius: 12, background: "var(--layer1)", border: "1px solid var(--border)" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", letterSpacing: 1, marginBottom: 4 }}>CALL VS PUT PAYOFF @ EXPIRY</div>
              <div style={{ fontSize: 9, color: "var(--dim)", marginBottom: 10 }}>ATM ${dive?.atmStrike} · premium ~${dive?.atmPremium} · ±10% spot range</div>
              <div style={{ height: 160 }}>
                <Line data={diveChart} options={{
                  responsive: true, maintainAspectRatio: false,
                  plugins: {
                    legend: { display: true, labels: { color: "#ffffff", font: { size: 9 }, boxWidth: 10, boxHeight: 10 }, align: "end" },
                    tooltip: { mode: "index", intersect: false },
                  },
                  scales: {
                    x: { ticks: { color: "#ffffff", font: { size: 8 }, maxTicksLimit: 6 }, grid: { color: "rgba(255,255,255,0.18)" } },
                    y: { ticks: { color: "#ffffff", font: { size: 9 }, callback: v => `$${v}` }, grid: { color: "rgba(255,255,255,0.18)" } },
                  },
                }} />
              </div>
            </div>
          )}

          {/* Read more */}
          {(dive?.readMoreUrl || notif.data?.url) && (
            <a data-testid="desktop-deep-dive-read-more" href={dive?.readMoreUrl || notif.data?.url} target="_blank" rel="noreferrer"
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, textDecoration: "none", padding: "10px 14px", borderRadius: 10, background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.25)", color: "#60a5fa", fontSize: 11, fontWeight: 700 }}>
              Read full story {dive?.source ? `on ${dive.source}` : ""} →
            </a>
          )}

          {/* Empty state */}
          {!dive && !notif.insight && !loading && (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, color: "var(--dim)", fontSize: 11, textAlign: "center", padding: 24 }}>
              <span style={{ fontSize: 28, opacity: 0.3 }}>Ω</span>
              <div>No AI insight available for this alert type.</div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

// ─── Position Monitor (Live P&L Dashboard) ───────────────────────────────────
function PositionMonitor() {
  const [data, setData] = useState(null);
  const [detailPos, setDetailPos] = useState(null);

  useEffect(() => {
    const load = () => axios.get(`${API}/positions/live`).then(r => setData(r.data)).catch(() => {});
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  if (!data || data.openCount === 0) {
    return (
      <div className="card" style={{ borderColor: "rgba(59,130,246,0.15)" }}>
        <div className="sh">Autopilot Position Monitor</div>
        <div style={{ textAlign: "center", padding: 16, color: "var(--dim)", fontSize: 12 }}>No open autopilot positions. Run a scan and execute to start monitoring.</div>
      </div>
    );
  }

  return (
    <>
    <div className="card" style={{ borderColor: data.totalPnl >= 0 ? "rgba(74,222,128,0.2)" : "rgba(248,113,113,0.2)" }}>
      <div className="row">
        <div className="sh" style={{ margin: 0 }}>Autopilot Position Monitor <span className="tag t-blue" style={{ fontSize: 8 }}>Click for details</span></div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span className={`tag ${data.totalPnl >= 0 ? "t-bull" : "t-bear"}`} style={{ fontSize: 11 }}>P&L: {fmt$(data.totalPnl)}</span>
          <span style={{ fontSize: 10, color: "var(--muted)" }}>{data.openCount} open | Value: {fmt$(data.totalValue)}</span>
        </div>
      </div>
      {data.positions.map((p, i) => (
        <div key={i} data-testid={`autopilot-pos-${i}`}
          style={{ background: "var(--layer2)", borderRadius: 10, padding: 12, marginTop: 8,
            borderLeft: `3px solid ${p.pnl >= 0 ? "var(--green)" : "var(--red)"}`, cursor: "pointer", transition: "background 0.15s" }}
          onClick={() => setDetailPos({ ...p, source: "autopilot", symbol: `${p.ticker} $${p.strike} ${p.optionType?.toUpperCase()}` })}
          onMouseOver={e => e.currentTarget.style.background = "rgba(56,189,248,0.06)"}
          onMouseOut={e => e.currentTarget.style.background = "var(--layer2)"}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{p.ticker}</span>
              <span style={{ fontSize: 12 }}>${p.strike} {p.optionType?.toUpperCase()}</span>
              <span className={`tag ${p.ziClass === "ULTRA_GEM" ? "t-bull" : p.ziClass === "GEM" ? "t-blue" : "t-neu"}`} style={{ fontSize: 8 }}>Zi {p.ziScore}</span>
              <span className={`tag ${p.tradeStrategy === "day_trade" ? "t-neu" : "t-pur"}`} style={{ fontSize: 8 }}>{p.tradeStrategy === "day_trade" ? "DAY" : "SWING"}</span>
              {p.isPaper ? (
                <span style={{ padding: "1px 5px", borderRadius: 3, fontSize: 8, fontWeight: 700, background: "rgba(251,191,36,0.15)", color: "#fbbf24", border: "1px solid rgba(251,191,36,0.3)" }}>z$</span>
              ) : (
                <span style={{ padding: "1px 5px", borderRadius: 3, fontSize: 8, fontWeight: 700, background: "rgba(74,222,128,0.15)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.3)" }}>USD</span>
              )}
            </div>
            <div style={{ textAlign: "right" }}>
              <div className={p.pnl >= 0 ? "pos" : "neg"} style={{ fontSize: 16, fontWeight: 600 }}>{p.pnl >= 0 ? "+" : ""}{fmt$(p.pnl)}</div>
              <div className={p.pnlPct >= 0 ? "pos" : "neg"} style={{ fontSize: 11 }}>{p.pnlPct >= 0 ? "+" : ""}{fmtN(p.pnlPct, 1)}%</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, fontSize: 10, color: "var(--muted)", flexWrap: "wrap" }}>
            <span>Entry: ${fmtN(p.entryPrice, 2)}</span>
            <span>Stock: ${fmtN(p.currentPrice, 2)}</span>
            <span>Hold: {p.holdHours < 24 ? `${fmtN(p.holdHours, 1)}h` : `${Math.round(p.holdHours / 24)}d`}</span>
            <span>Value: {fmt$(p.positionValue)}</span>
            {p.dteRemaining != null && <span style={{ color: p.dteRemaining <= 7 ? "#f87171" : p.dteRemaining <= 21 ? "#fbbf24" : "var(--muted)" }}>DTE: {p.dteRemaining}d</span>}
            {p.at21DTE && <span style={{ color: "#fbbf24", fontWeight: 700 }}>21 DTE — consider closing</span>}
            {p.atProfitTarget && <span style={{ color: "var(--green)", fontWeight: 600 }}>AT PROFIT TARGET</span>}
            {p.atStopLoss && <span style={{ color: "var(--red)", fontWeight: 600 }}>AT STOP LOSS</span>}
          </div>
        </div>
      ))}
    </div>
    {detailPos && <PositionDetailModal position={detailPos} onClose={() => setDetailPos(null)} />}
    </>
  );
}


// ─── Live Scanner Feed (SSE Streaming Autopilot Thinking) ────────────────────




// ─── Broker Settings Panel ────────────────────────────────────────────────────


function MobileWorkspace() {
  const navigate = useNavigate();
  const [mobileUiMode, setMobileUiModeState] = useState(() => localStorage.getItem("zi_mobileUiMode") || "swipe3");

  // Boot gate: only Diamond currently reports back when its first data
  // round trip lands (see DiamondUI's onReady), so it's the only mode this
  // holds the branded overlay for on first mount — the others keep their
  // prior behavior (Suspense fallback only, then their own screen renders
  // and fills in as it goes) rather than guessing a fake "ready" signal for
  // a screen that never reports one. Read once at mount; switching modes
  // later never re-triggers this (that's a normal in-app nav, not a boot).
  // The boot overlay itself is owned by AppRouter's BootOverlay (one element
  // for the whole launch — see lib/bootGate.js for why); Diamond's real,
  // named milestones (connecting, roster, board, wire, history — never a
  // fabricated percentage) and its ready signal are forwarded to that store.
  const gate = useBootGate();
  const handleDiamondReady = useCallback(() => bootGate.setReady(), []);
  const handleDiamondBootStage = useCallback((stage) => bootGate.setStage(stage), []);

  const setMobileUiMode = (mode) => {
    localStorage.setItem("zi_mobileUiMode", mode);
    setMobileUiModeState(mode);
    window.dispatchEvent(new CustomEvent("zi-ui-mode-changed"));
    // swipe/swipe2/swipe3/glass/diamond live on /mobile; any other mode (advanced,
    // floor) is the desktop Dashboard route — leave /mobile so AppRouter keeps
    // them on "/".
    if (mode !== "swipe" && mode !== "swipe2" && mode !== "swipe3" && mode !== "glass" && mode !== "diamond") {
      navigate("/", { replace: true });
    }
  };

  // Exactly one of these ever renders at a time, and 3 of the 4 are lazy
  // chunks (see the imports above) — one Suspense boundary around whichever
  // gets picked, rather than one per branch.
  let screen;
  if (mobileUiMode === "diamond") {
    // No onClose — Diamond's own Settings tab is the way out (switching UI
    // mode re-routes), so it doesn't need a separate dismiss handler.
    screen = <DiamondUI setMobileUiMode={setMobileUiMode} onReady={handleDiamondReady} onBootStage={handleDiamondBootStage} />;
  } else if (mobileUiMode === "swipe2") {
    screen = (
      <SwipeCards2
        onClose={() => navigate("/", { replace: true })}
        setMobileUiMode={setMobileUiMode}
        mobileUiMode={mobileUiMode}
      />
    );
  } else if (mobileUiMode === "swipe3" || mobileUiMode === "glass") {
    screen = (
      <SwipeCards3
        onClose={() => navigate("/", { replace: true })}
        setMobileUiMode={setMobileUiMode}
        mobileUiMode={mobileUiMode}
      />
    );
  } else {
    screen = (
      <SwipeCards
        onClose={() => navigate("/", { replace: true })}
        setMobileUiMode={setMobileUiMode}
        mobileUiMode={mobileUiMode}
      />
    );
  }
  // No Suspense fallback screen while the boot overlay is up: it sits
  // underneath the overlay anyway, and its own aurora/ring animations were
  // one more full-screen loading surface mounting and unmounting mid-launch.
  return <Suspense fallback={gate.active ? null : <LazyScreenFallback />}>{screen}</Suspense>;
}

function AppRouterInner() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const gate = useBootGate();

  // Responsive route sync. Two separate concerns, deliberately NOT merged into
  // one effect keyed on location.pathname — that used to re-run this check on
  // every navigation, including a desktop user explicitly clicking a "preview
  // mobile mode" button (Settings' UI Mode grid, MobileSettingsExpanded, the
  // sidebar's own View grid, etc.), which immediately
  // bounced them straight back to "/" before they ever saw the mode they
  // picked — e.g. selecting "UI Glass" from Settings never actually landed
  // on UI Glass.
  //
  // 1) One-time correction on mount/login: a narrow-screen user landing on
  //    "/" gets moved into their saved mobile mode.
  // 2) A real resize/rotation listener (not route-driven) auto-hops between
  //    "/" and "/mobile" as the viewport actually crosses the breakpoint.
  // Neither reacts to a plain route change, so explicit in-app navigation to
  // "/mobile" — at any viewport width — is respected instead of fought.
  useEffect(() => {
    if (loading || !user || typeof window === "undefined") return;
    const narrow = window.matchMedia("(max-width: 768px)").matches;
    const uiMode = localStorage.getItem("zi_mobileUiMode") || "swipe3";
    const isMobileMode = uiMode === "swipe" || uiMode === "swipe2" || uiMode === "swipe3" || uiMode === "glass" || uiMode === "diamond";
    if (narrow && isMobileMode && location.pathname !== "/mobile") {
      navigate("/mobile", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => {
      const uiMode = localStorage.getItem("zi_mobileUiMode") || "swipe3";
      const isMobileMode = uiMode === "swipe" || uiMode === "swipe2" || uiMode === "swipe3" || uiMode === "glass" || uiMode === "diamond";
      const onMobile = window.location.pathname === "/mobile";
      if (e.matches && isMobileMode && !onMobile) {
        navigate("/mobile", { replace: true });
      } else if (!e.matches && onMobile) {
        navigate("/", { replace: true });
      }
    };
    mq.addEventListener ? mq.addEventListener("change", handler) : mq.addListener(handler);
    return () => mq.removeEventListener ? mq.removeEventListener("change", handler) : mq.removeListener(handler);
  }, [navigate]);

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  if (loading) {
    // During a Diamond mobile launch the boot overlay (BootOverlay, below)
    // already covers the screen with the same branding, labelled "Signing
    // you in" — a second, differently-placed loading screen here was the
    // first of the launch's visible hand-off glitches.
    return gate.active ? null : <OmegaLoadingScreen label="Loading Omega ZI AI" />;
  }

  // Dedicated admin sign-in route — bypasses the landing page modal entirely.
  if (location.pathname === "/admin") {
    return <AdminLogin />;
  }

  // Public Omega Tea feed — no auth required (like a marketing/landing page).
  if (location.pathname === "/tea") {
    return <OmegaTeaPage />;
  }

  // Default: when not logged in, show the original marketing landing page
  // (Administrator button in the top-right opens the full login modal with
  // forgot-password and recovery flows). The simple admin form remains
  // accessible at /admin for quick access from bookmarks.
  if (!user) {
    return <LoginPage />;
  }

  return (
    <>
      <GuardianToast user={user} />
      <AskOmegaFAB />
      <Routes>
        <Route path="/mobile" element={<MobileWorkspace />} />
        <Route path="/market/live" element={
          <ErrorBoundary label="Live market view crashed"><LiveMarketDashboard /></ErrorBoundary>
        } />
        <Route path="/control/oci" element={
          <ErrorBoundary label="OCI panel crashed"><OCIControlCenter /></ErrorBoundary>
        } />
        <Route path="/tea" element={
          <ErrorBoundary label="Omega Tea page crashed"><OmegaTeaPage /></ErrorBoundary>
        } />
        <Route path="*" element={<Dashboard />} />
      </Routes>
    </>
  );
}

// One boot overlay for the whole mobile launch (auth → lazy chunk → Diamond),
// rendered as AppRouterInner's sibling so React keeps the same DOM node
// through every hand-off; it exits in three beats — bar fills, fade, unmount
// after the fade has fully played — see lib/bootGate.js.
function BootOverlay() {
  const gate = useBootGate();
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!gate.active) return undefined;
    const t = setTimeout(() => bootGate.setReady(), BOOT_SAFETY_MS);
    return () => clearTimeout(t);
  }, [gate.active]);
  useEffect(() => {
    if (!gate.active || !gate.ready) return undefined;
    const fill = setTimeout(() => setDone(true), BOOT_FILL_MS);
    const remove = setTimeout(() => bootGate.finish(), BOOT_UNMOUNT_MS);
    return () => { clearTimeout(fill); clearTimeout(remove); };
  }, [gate.active, gate.ready]);
  if (!gate.active) return null;
  return <OmegaLoadingScreen label={BOOT_STAGE_LABELS[gate.stage] || BOOT_STAGE_LABELS.connecting} overlay done={done} />;
}

function AppRouter() {
  useState(() => { bootGate.start(isDiamondMobileBoot()); return true; });
  return (
    <>
      <AppRouterInner />
      <BootOverlay />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PreloadProvider>
          <ThemeManager />
          <ErrorBoundary label="Something went wrong — the app hit an unexpected error">
            <AppRouter />
          </ErrorBoundary>
          <PwaInstallBanner />
          <Toaster position="top-right" richColors closeButton data-testid="omega-toaster" />
        </PreloadProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
